// B3 — Decide rail (right, 320px), functional.
//
// Recommendation-first (UX rule #6): the recommended action is one
// click; choosing a different action asks for a one-line rationale
// (never blocks). Every action shows a plain-language before→after
// sentence (UX rule #3). Reversible actions surface a 60-second Undo
// (UX rule #7) — beyond that they remain reversible via the record's
// History tab (substrate doctrine). Delegates to the already-deployed
// POST /api/cleanup/decide + /undo (A9). B4 layers the rich
// restricted / no-dead-end states on top of the error surface here.

import "./decide-rail.css";
import { useEffect, useState } from "react";
import { ExternalLink, Pencil, UserCheck, Zap } from "lucide-react";

import { QuarantineMatchPanel } from "./QuarantineMatchPanel";
import { RetentionDispositionPanel } from "./RetentionDispositionPanel";
import { addHouseholdMemberAnswer } from "./add-household-member-answer";
import { recordDeepLink } from "./record-links";

import {
  api,
  ApiError,
  type AssignableUser,
  type CleanupAuthz,
  type CleanupIssueSummary,
} from "../api";
import { Button, Row } from "../ui";
import {
  contactClusterRefs,
  contactMoveSource,
  displayLabel,
  duplicateMatchType,
  householdClusterRefs,
  householdSearchMergeSource,
  isImportStubWithCandidate,
  isUnconfirmedSplitFamily,
  loanHouseholdMergeAlternative,
  realtyLinkSource,
  recordMoveTarget,
  stubTwinChoices,
  type RecordMoveTarget,
  type RecordRef,
} from "./cleanup-records";
import {
  ivansCreatePolicySource,
  ivansSuggestedClientName,
  ivansSuggestedHousehold,
  ivansSuggestedPolicyType,
} from "./ivans-create-policy-predicate";
import { livesAloneAnswer } from "./lives-alone-answer";
import { spouseNotAClientAnswer } from "./spouse-not-a-client-answer";
import {
  RECON_CONSEQUENCE,
  type ReconVerb,
} from "./recon-verbs";
import {
  expirationConsequence,
  expirationDateRequired,
} from "./expiration-issue";
import {
  PM_ENTITY_INDIVIDUAL_LABEL,
  PM_ENTITY_KEEP_PERSONAL_LABEL,
  type PmEntityKeepReason,
  isPmEntityIssue,
  isValidEntityNameInput,
  pmEntityConsequence,
  pmEntityIndividualConsequence,
  pmEntityKeepPersonalConsequence,
} from "./pm-entity-issue";
import {
  isExpirationIssue,
  isValidExpirationInput,
} from "./expiration-issue";
import {
  CLIENT_SINCE_UNKNOWN_LABEL,
  clientSinceConsequence,
  clientSinceUnknownConsequence,
  isClientSinceIssue,
  isValidClientSinceInput,
} from "./client-since-issue";
import { DateField } from "../components/DateField";
import {
  duplicatePolicyConsequence,
  duplicatePolicyRows,
  isDuplicatePolicyIssue,
} from "./insurance-duplicate-policy-issue";
import {
  MARITAL_OPTIONS,
  MARITAL_UNKNOWN_LABEL,
  isMaritalIssue,
  isValidMaritalInput,
  maritalConsequence,
  maritalUnknownConsequence,
} from "./marital-issue";
import {
  PRIMARY_UNKNOWN_LABEL,
  type PrimaryCandidate,
  isPrimaryIssue,
  isValidPrimaryPick,
  primaryPickApplyLabel,
  primaryPickConsequence,
  primaryPickPrompt,
  primaryUnknownConsequence,
} from "./primary-issue";
import {
  TRADITIONAL_LOAN_PROGRAM_TYPE_OPTIONS,
  isProgramTypeIssue,
  isValidProgramTypeInput,
  programTypeConsequence,
  programTypeLabel,
} from "./program-type-issue";
import {
  effectiveDateConsequence,
  isEffectiveDateIssue,
  isValidEffectiveDateInput,
} from "./effective-date-issue";
import {
  PM_FEE_UNKNOWN_LABEL,
  feeInputToCents,
  isPmFeeIssue,
  isValidFeeInput,
  pmFeeConsequence,
  pmFeeUnknownConsequence,
} from "./pm-fee-issue";
import {
  isRealtyPriceIssue,
  isValidSalePriceInput,
  parseSalePriceInput,
  realtyPriceConsequence,
} from "./realty-price-issue";
import {
  COMMISSION_APPLY_LABEL,
  COMMISSION_FLOW_LINK_LABEL,
  commissionFlowPath,
  isCommissionIssue,
} from "./commission-issue";
import {
  QUARANTINE_ARCHIVE_LABEL,
  QUARANTINE_KEEP_LABEL,
  QUARANTINE_MERGE_LABEL,
  isQuarantineIssue,
  quarantineArchiveConsequence,
  quarantineKeepConsequence,
} from "./quarantine-issue";
import {
  VENDOR_LOOKALIKE_CLIENT_LABEL,
  VENDOR_LOOKALIKE_PERSON_LABEL,
  VENDOR_LOOKALIKE_RETAG_LABEL,
  isVendorLookalikeIssue,
  vendorLookalikeClientEntityConsequence,
  vendorLookalikePersonConsequence,
} from "./vendor-lookalike-issue";
import {
  COMBINED_CONTACT_ARCHIVE_LABEL,
  combinedContactArchiveConsequence,
  combinedContactPrefersArchive,
  combinedContactSplitConsequence,
  combinedContactSplitLabel,
  isCombinedContactIssue,
} from "./combined-contact-issue";
import {
  ARCHIVED_MEMBER_CLOSED_LABEL,
  ARCHIVED_MEMBER_UNARCHIVE_LABEL,
  archivedMemberClosedConsequence,
  archivedMemberPrefersArchive,
  archivedMemberUnarchiveConsequence,
  isArchivedMemberIssue,
} from "./archived-member-issue";
import {
  EFFECTIVE_DATE_UNKNOWN_LABEL,
  FUNDED_DATE_UNKNOWN_LABEL,
  NO_CONTACT_METHOD_KEEP_LABEL,
  REALTY_STILL_ACTIVE_LABEL,
  addContactMethodConsequence,
  effectiveDateUnknownConsequence,
  fundedDateUnknownConsequence,
  isEffectiveDateUnknownIssue,
  isFundedDateUnknownIssue,
  isNoContactMethodIssue,
  isRealtyStillActiveIssue,
  noContactMethodKeepConsequence,
  realtyStillActiveConsequence,
} from "./stop-asking-issues";
import { REASON_CODE_HELP } from "./cleanup-glossary";
// `Verb` + `applicability` extracted to a pure module so the one-click
// dispatch logic is unit-testable without React (see
// tests/web/decide-rail-applicability.test.ts). Per PR #551 + this PR:
// merge now honors `recommendation.params.survivor_id` when the
// detector states a directional intent (e.g., mortgage stub detector
// emits [stub, candidate] with the candidate as survivor); falls back
// to recs[0]=survivor for order-agnostic detectors.
import {
  applicability,
  choiceApplyLabel,
  choiceVerbNoun,
  type Verb,
} from "./decide-rail-applicability";
import {
  canOfferLinkHousehold,
  planLinkHouseholds,
  type LinkHouseholdPlan,
} from "./link-household";
import {
  VENDOR_SUBTYPES,
  VENDOR_SUBTYPE_LABELS,
} from "../../../shared/types/vendor";
import { isAdvisorAssignmentSlug } from "../../../shared/cleanup/detector-slugs";
import { companyLabel } from "../components/permissions/company-colors";

// Client-facing companies a vendor contact can be associated with
// (Baker 2026-06-27). Productions is a support entity — not offered.
const VENDOR_COMPANY_OPTIONS = [
  "mortgage",
  "insurance",
  "planning",
  "realty",
  "financial",
] as const;

// Audit 6.2 — advisor-gap detectors reuse the recon verb "match" but
// their action is really "assign this advisor to the record". Keyed
// off the shared detector-slug suffix so all five companies' gap
// detectors get the honest copy + the picker flow.
function isAdvisorGapIssue(issue: CleanupIssueSummary): boolean {
  return isAdvisorAssignmentSlug(issue.detectorSlug);
}

// Plain-language before→after, UX rule #3 (never a raw field diff).
function consequence(
  verb: Verb,
  issue: CleanupIssueSummary,
  advisorName?: string | null,
  expirationDate?: string,
  clientSinceDate?: string,
  effectiveDate?: string,
  entityName?: string,
  clientSinceIsFloor?: boolean,
  programType?: string,
  programTypeNote?: string,
  maritalStatus?: string,
  feeAmount?: string,
  contactEmail?: string,
  contactPhone?: string,
  salePrice?: string,
  primaryPick?: string,
  primaryCandidates?: readonly PrimaryCandidate[],
): string {
  const recs = issue.affectedRecords;
  // Baker 2026-07-31 — realty zero-price: the button RECORDS the sale
  // price on the property record; it no longer resolves the card with
  // nothing written.
  if (verb === "verify" && isRealtyPriceIssue(issue)) {
    const copy = realtyPriceConsequence(issue, salePrice ?? "");
    if (copy) return copy;
  }
  // Baker 2026-07-13 — pm revenue gap: the button SETS the amount earned
  // on the loan; the escape records the durable "can't determine" answer.
  if (verb === "verify" && isPmFeeIssue(issue)) {
    const copy = pmFeeConsequence(issue, feeAmount ?? "");
    if (copy) return copy;
  }
  if (verb === "keep-current" && isPmFeeIssue(issue)) {
    return pmFeeUnknownConsequence();
  }
  // Baker 2026-07-13 — hh-quarantined: keep-current clears the quarantine
  // flag ("it's a real household"); archive is the "not a real household"
  // answer. The merge answer opens the picker (no verb from here).
  if (verb === "keep-current" && isQuarantineIssue(issue)) {
    return quarantineKeepConsequence();
  }
  if (verb === "archive" && isQuarantineIssue(issue)) {
    return quarantineArchiveConsequence();
  }
  // Baker 2026-07-13 — vendor-lookalike: dismiss means "it's a person".
  if (verb === "dismiss" && isVendorLookalikeIssue(issue)) {
    return vendorLookalikePersonConsequence();
  }
  // Grant 2026-08-10 — vendor-lookalike: keep-current means "it's a
  // business we SERVE" (a client entity), not the generic recon-verb
  // "keep the current value".
  if (verb === "keep-current" && isVendorLookalikeIssue(issue)) {
    return vendorLookalikeClientEntityConsequence();
  }
  // Donna 2026-07-30 — combined-contact: verify SPLITS the record into
  // two contacts; archive is the "redundant combined record" answer.
  if (verb === "verify" && isCombinedContactIssue(issue)) {
    return combinedContactSplitConsequence();
  }
  if (verb === "archive" && isCombinedContactIssue(issue)) {
    return combinedContactArchiveConsequence(
      combinedContactPrefersArchive(issue),
    );
  }
  // Donna + Baker 2026-07-30 — hh-empty archived-member variant: verify
  // UN-ARCHIVES the member; archive is the "genuinely closed" answer.
  if (verb === "verify" && isArchivedMemberIssue(issue)) {
    return archivedMemberUnarchiveConsequence();
  }
  if (verb === "archive" && isArchivedMemberIssue(issue)) {
    return archivedMemberClosedConsequence(archivedMemberPrefersArchive(issue));
  }
  // Audit 6.2 — without this, the generic "match" copy ("Confirms
  // these are the same and links them") bled into advisor-gap issues,
  // describing a different action than the button performs.
  if (verb === "match" && isAdvisorGapIssue(issue)) {
    const who = advisorName || "the advisor you pick";
    return `Sets ${who} as the advisor on this record — they become accountable for the client. The issue resolves; reversible for 60 seconds (undo removes the assignment and reopens the issue).`;
  }
  // Grant 2026-07-11 — pm-entity-name-needed: the button CREATES the
  // business entity and moves the loans (verify), or records the honest
  // "not known — keep under the personal name" answer (keep-current).
  if (verb === "verify" && isPmEntityIssue(issue)) {
    const copy = pmEntityConsequence(issue, entityName ?? "");
    if (copy) return copy;
  }
  if (verb === "keep-current" && isPmEntityIssue(issue)) {
    return pmEntityKeepPersonalConsequence();
  }
  // Joy 2026-07-01 — same doctrine for the expiration issues: the
  // generic "Marks this verified" copy hid that the button actually
  // SETS the date on the policy.
  if (verb === "verify" && isExpirationIssue(issue)) {
    const copy = expirationConsequence(issue, expirationDate ?? "");
    if (copy) return copy;
  }
  // Nate 2026-07-02 — same doctrine for the client-since issue: the
  // button SETS the date on the household's company relationship.
  // Grant 2026-07-12: floor-aware copy + the "we don't know" escape.
  if (verb === "verify" && isClientSinceIssue(issue)) {
    const copy = clientSinceConsequence(
      issue,
      clientSinceDate ?? "",
      clientSinceIsFloor === true,
    );
    if (copy) return copy;
  }
  if (verb === "keep-current" && isClientSinceIssue(issue)) {
    return clientSinceUnknownConsequence();
  }
  // Grant 2026-07-13 — marital status: "verify" SETS the status on the
  // primary contact; "keep-current" records the durable "we don't know".
  if (verb === "verify" && isMaritalIssue(issue)) {
    return maritalConsequence(maritalStatus ?? "");
  }
  if (verb === "keep-current" && isMaritalIssue(issue)) {
    return maritalUnknownConsequence();
  }
  // Baker 2026-07-31 — hh-no-primary: "verify" SETS the picked member as
  // the household's one primary (demoting any other claimant);
  // "keep-current" records the durable "we don't know".
  if (verb === "verify" && isPrimaryIssue(issue)) {
    return primaryPickConsequence(primaryPick ?? "", primaryCandidates ?? []);
  }
  if (verb === "keep-current" && isPrimaryIssue(issue)) {
    return primaryUnknownConsequence();
  }
  // Grant-approved 2026-07-14 — the three "stop asking" escapes: the
  // recommended action still fixes the record; "keep-current" is the
  // honest terminal answer when there's nothing to recover.
  if (verb === "keep-current" && isNoContactMethodIssue(issue)) {
    return noContactMethodKeepConsequence();
  }
  // P1-6 (cleanup UX audit, 2026-07-13) — the button ADDS the typed
  // email/phone to the contact.
  if (verb === "verify" && isNoContactMethodIssue(issue)) {
    return addContactMethodConsequence(
      contactEmail ?? "",
      contactPhone ?? "",
    );
  }
  if (verb === "keep-current" && isEffectiveDateUnknownIssue(issue)) {
    return effectiveDateUnknownConsequence();
  }
  if (verb === "keep-current" && isFundedDateUnknownIssue(issue)) {
    return fundedDateUnknownConsequence();
  }
  if (verb === "keep-current" && isRealtyStillActiveIssue(issue)) {
    return realtyStillActiveConsequence();
  }
  // Grant 2026-07-12 — same doctrine for the program-type issue: the
  // button SETS the loan's program type from the full picker.
  if (verb === "verify" && isProgramTypeIssue(issue)) {
    const copy = programTypeConsequence(
      issue,
      programType ?? "",
      programTypeNote ?? "",
    );
    if (copy) return copy;
  }
  // Grant 2026-07-10 QA — same doctrine for the missing-effective-date
  // issue: the button SETS the record's start date.
  if (verb === "verify" && isEffectiveDateIssue(issue)) {
    const copy = effectiveDateConsequence(issue, effectiveDate ?? "");
    if (copy) return copy;
  }
  switch (verb) {
    case "merge": {
      const survivor = displayLabel(recs[0]?.label ?? "") || "the first record";
      const merged = displayLabel(recs[1]?.label ?? "") || "the other record";
      const kind = issue.entityKind;
      if (kind === "household") {
        return `Merges “${merged}” into “${survivor}”. All members, services, opportunities, transactions, and activity move under the surviving household; nothing is deleted. Reversible from the household’s History tab.`;
      }
      return `Merges “${merged}” into “${survivor}”. Contact methods, notes, tags, and activity move to the survivor — nothing is deleted. Services, opportunities, and transactions are household-scoped, so they stay with their household; if the two are in different households, merge the households to consolidate everything. Reversible for 60 seconds, and from History after that.`;
    }
    case "recase": {
      // The detector set affectedRecords[0].label to the proposed
      // (already-fixed) name, so this reads as the after-state.
      const fixed = displayLabel(recs[0]?.label ?? "") || "display form";
      return `Rewrites the ALL-CAPS name to “${fixed}” using Stewardship’s shared name normalizer. Only the name casing changes; the edit is versioned and reversible from the contact’s History tab.`;
    }
    case "resolve-trust-reattach":
      return "Server re-validates this quarantined financial record, then either merges it into the confidently-matched family household or clears its quarantine flag (a person/joint account with no family genuinely is its own household). Reversible + audited via the merge service. If it needs splitting first, nothing changes and you’re told why.";
    case "archive":
    // Same server handler (`case "archive": case "archive-empty-household":`),
    // so the same sentence. Named separately only so the empty-shell branch
    // alone could join AUTO_EXECUTABLE_VERBS.
    case "archive-empty-household":
      return "Archives the chosen record — removed from every list and view, but the row and a version snapshot are kept, so it’s fully restorable from its History / Archive. Reversible for 60 seconds here, and any time after that.";
    case "dismiss":
      return "Marks this as “not an issue.” It leaves the queue but is fully reversible — you (or anyone) can reopen it later.";
    case "acknowledge":
      return "Marks this reviewed and clears it from your list. Nothing about the record changes — this only affects the cleanup queue. Reversible for 60 seconds (it reopens).";
    case "snooze":
      return "Hides this for 24 hours, then it returns to the queue automatically. Nothing changes on the records.";
    case "retag-as-vendor":
      return "Reclassifies the chosen record as a vendor (title officer, lender, partner, etc.). It stops appearing in person-facing surfaces + drops out of duplicate-detector clusters on the next reconcile. Reversible via History on the contact.";
    // 2026-09-11 — a real product with no relationship row behind it.
    // The row is derived from the household's records by the same call
    // every create path makes; nothing on the product changes.
    case "create-relationship-row":
      return "Adds the company relationship this household is missing, derived from its own records the way every normal save does: Active Client, fit not yet assessed, no advisor, client-since from the earliest record. Nothing on the policy, loan, or account changes. After this the household counts as a client of that company on every list, filter, and report.";
    // ── E1 reconciliation vocabulary (single source of truth in
    //    recon-verbs.ts; unit-tested there) ──────────────────────────
    case "match":
    case "accept-variance":
    case "adjust":
    case "verify":
    case "mark-historical":
    case "keep-current":
    case "accept-proposed":
      return RECON_CONSEQUENCE[verb as ReconVerb];
    // Grant 2026-08-18 — the carrier sent a policy we don't have. The
    // picker takes the destination; every figure comes from the
    // carrier's own download.
    case "create-policy":
      return "Adds the carrier's policy to the household you pick — carrier, policy number, coverage, term dates and premium exactly as the carrier sent them. The policy joins the Renewal Plan and the household becomes an Insurance client. Reversible for 60 seconds; afterwards, archive it from the record's own page.";
  }
}

// Audit 6.1 — the post-action strip's one-line summary. Names the
// verb AND the object ("Assigned Ryan Delviken — Medicare Case —
// Garcia Family"), never just "done".
const ACTION_SUMMARY_VERB: Record<Verb, string> = {
  "create-policy": "Added the carrier's policy",
  merge: "Merged",
  dismiss: "Marked not an issue",
  acknowledge: "Acknowledged",
  snooze: "Snoozed for 24 hours",
  recase: "Fixed name casing",
  archive: "Archived",
  "archive-empty-household": "Archived the empty household",
  "retag-as-vendor": "Retagged as vendor",
  "create-relationship-row": "Added the company relationship",
  "resolve-trust-reattach": "Re-validated",
  match: "Matched",
  "accept-variance": "Accepted the variance",
  adjust: "Marked for adjustment",
  verify: "Verified",
  "mark-historical": "Marked historical",
  "keep-current": "Kept the current value",
  "accept-proposed": "Accepted the proposed value",
};

function actionSummary(
  verb: Verb,
  issue: CleanupIssueSummary,
  advisorName: string | null,
): string {
  const target =
    displayLabel(issue.affectedRecords[0]?.label ?? "") ||
    issue.decisionQuestion;
  if (verb === "match" && isAdvisorGapIssue(issue)) {
    return `Assigned ${advisorName ?? "an advisor"} — ${target}`;
  }
  // Grant 2026-07-11 — pm-entity issues: name the actual outcome, not
  // the generic verb noun.
  if (isPmEntityIssue(issue)) {
    if (verb === "verify") return `Created the entity & moved the loans — ${target}`;
    if (verb === "keep-current") {
      return `Recorded “not known — keep under personal name” — ${target}`;
    }
  }
  // Grant 2026-07-12 — client-since "we don't know" escape.
  if (isClientSinceIssue(issue) && verb === "keep-current") {
    return `Recorded “we don't know when they became a client” — ${target}`;
  }
  // Grant 2026-07-13 — marital / primary "we don't know" escapes.
  if (isMaritalIssue(issue) && verb === "keep-current") {
    return `Recorded “we don't know their marital status” — ${target}`;
  }
  if (isPrimaryIssue(issue) && verb === "keep-current") {
    return `Recorded “we don't know the primary contact” — ${target}`;
  }
  if (isRealtyPriceIssue(issue) && verb === "verify") {
    return `Recorded the sale price — ${target}`;
  }
  // Baker 2026-07-13 — the three dead-end fixes: name the outcome.
  if (isPmFeeIssue(issue)) {
    if (verb === "verify") return `Set the amount earned — ${target}`;
    if (verb === "keep-current") {
      return `Recorded “we can't determine this fee” — ${target}`;
    }
  }
  if (isQuarantineIssue(issue) && verb === "keep-current") {
    return `Confirmed as a real household (flag cleared) — ${target}`;
  }
  if (isVendorLookalikeIssue(issue)) {
    if (verb === "retag-as-vendor") return `Retagged as a vendor — ${target}`;
    if (verb === "dismiss") return `Confirmed it's a person — ${target}`;
    if (verb === "keep-current") {
      return `Recorded “a business we serve, not a vendor” — ${target}`;
    }
  }
  // Grant-approved 2026-07-14 — the three "stop asking" escapes.
  if (verb === "keep-current") {
    if (isNoContactMethodIssue(issue)) {
      return `Recorded “no email or phone — keep record anyway” — ${target}`;
    }
    if (isEffectiveDateUnknownIssue(issue)) {
      return `Recorded “start date not recoverable” — ${target}`;
    }
    if (isFundedDateUnknownIssue(issue)) {
      return `Recorded “funded date not recoverable” — ${target}`;
    }
    if (isRealtyStillActiveIssue(issue)) {
      return `Recorded “still an active listing — not under contract yet” — ${target}`;
    }
  }
  return `${ACTION_SUMMARY_VERB[verb] ?? verb} — ${target}`;
}

// What the page-level strip needs to undo + label one decision.
export interface DecideActedEvent {
  issueId: string;
  verb: string;
  summary: string;
  undo:
    | { available: true; kind: string; refs: Record<string, unknown> }
    | { available: false };
}

// Audit 6.1 — terminal states get a Resolved panel with a permanent
// Reopen (the dismiss copy promises it; the rail previously rendered
// the normal action buttons against an already-resolved issue).
const TERMINAL_STATES = new Set([
  "resolved",
  "dismissed",
  "auto-resolved",
  "rolled-back",
]);

function ResolvedPanel({
  issue,
  onResolved,
}: {
  issue: CleanupIssueSummary;
  onResolved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const stateLabel =
    issue.state === "dismissed"
      ? "Dismissed — marked not an issue"
      : issue.state === "auto-resolved"
        ? "Auto-resolved by the system"
        : issue.state === "rolled-back"
          ? "Rolled back"
          : "Resolved";

  async function reopen() {
    setBusy(true);
    setErr(null);
    try {
      await api.reopenCleanupIssue(issue.id);
      onResolved();
    } catch (e) {
      setErr(
        e instanceof ApiError ? e.message : "Couldn't reopen. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="cu-dr-s1">
        <b>{stateLabel}.</b> No further action is needed.
      </div>
      <div className="cu-dr-s2">
        Decided in error, or did the problem come back? Reopening puts
        it back in the open queue — it doesn’t change any records.
      </div>
      <Button variant="sw-default" onClick={reopen} disabled={busy}>
        {busy ? "Reopening…" : "Reopen this issue"}
      </Button>
      {err && (
        <div className="cu-dr-s3">
          {err}
        </div>
      )}
    </div>
  );
}

// Follow-up panel (Donna's request). Shown when the selected issue is
// PARKED in the "Waiting on follow-up" lane: it explains what it's
// waiting on and offers the one move that makes sense — bring it back
// to the queue (resume), at which point the normal action buttons
// return. Research stays fully available in the center pane.
function FollowupPanel({
  issue,
  onResolved,
}: {
  issue: CleanupIssueSummary;
  onResolved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function resume() {
    setBusy(true);
    setErr(null);
    try {
      await api.resumeCleanupIssueFollowup(issue.id);
      onResolved();
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? e.message
          : "Couldn't bring it back. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="cu-dr-s4">
        <b>Waiting on follow-up.</b> You set this aside while waiting on
        an answer. It's out of your active lanes until you bring it back.
      </div>
      {issue.followupReason && (
        <div className="cu-dr-s5">
          <span className="cu-dr-s6">
            What you're waiting on
          </span>
          {issue.followupReason}
        </div>
      )}
      <div className="cu-dr-s2">
        Heard back? Bring it back to the queue to act on it. Nothing on
        the records has changed.
      </div>
      <Button variant="primary" onClick={resume} disabled={busy}>
        {busy ? "Bringing it back…" : "Back to the queue"}
      </Button>
      {err && (
        <div className="cu-dr-s3">
          {err}
        </div>
      )}
    </div>
  );
}

/**
 * Add a note to this issue's thread. On EVERY issue.
 *
 * Mylah, 2026-08-19: "I'm also not seeing... the box to add a note."
 * She was right. A note box existed only inside RestrictedPanel — the
 * panel shown when a person CANNOT act on an issue. On an ordinary
 * issue, the one a steward is actually working, there was nowhere to
 * write anything down.
 *
 * That is backwards. The Cleanup doctrine says every issue carries a
 * note thread, and the API has always accepted one; only the box was
 * missing. And the person most likely to need it is exactly the one who
 * CAN act: "called the carrier, waiting to hear back", "this is the
 * daughter, not the insured" — the context that stops the next person
 * redoing the same research.
 *
 * Deliberately collapsed by default. The rail is the decide column and
 * the recommended action has to stay the obvious thing to do (UX rule
 * #6); a permanently-open textarea competes with it.
 */
function IssueNoteBox({ issue }: { issue: CleanupIssueSummary }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // A new issue is a new thread: never carry one issue's half-typed note
  // onto the next record in the queue.
  useEffect(() => {
    setOpen(false);
    setNote("");
    setMsg(null);
    setErr(null);
  }, [issue.id]);

  async function add() {
    const text = note.trim();
    if (!text) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await api.addCleanupIssueNote(issue.id, text);
      setNote("");
      setMsg("Note added to this item.");
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Couldn't add the note.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cu-dr-s7">
      {!open ? (
        <Button
          variant="sw-default"
          size="sm"
          onClick={() => setOpen(true)}
          style={{ width: "100%" }}
        >
          Add a note
        </Button>
      ) : (
        <>
          <label
            htmlFor="cu-issue-note" className="cu-dr-s8">
            Anything the next person should know — what you checked, who you
            called, what you're waiting on. Notes don't change the record.
          </label>
          <textarea
            id="cu-issue-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            disabled={busy} className="cu-dr-s9"
          />
          <Row gap={8}>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void add()}
              disabled={busy || note.trim().length === 0}
            >
              {busy ? "Adding…" : "Add note"}
            </Button>
            <Button
              variant="sw-default"
              size="sm"
              onClick={() => {
                setOpen(false);
                setNote("");
              }}
              disabled={busy}
            >
              Cancel
            </Button>
          </Row>
        </>
      )}
      {msg && (
        <div className="cu-dr-s10">
          {msg}
        </div>
      )}
      {err && (
        <div className="cu-dr-s11">
          {err}
        </div>
      )}
    </div>
  );
}

// B4 — the no-dead-end panel. Shown INSTEAD of the action buttons
// when the actor can't act on this issue (out-of-scope workflow
// entity). It states the boundary in plain language (the centralized
// canActOnEntity.reason — never invented here) and offers the forward
// paths: hand it to the right owner, and/or add a note. Research is
// untouched (a separate pane) — the user can still fully investigate.
function RestrictedPanel({
  issue,
  authz,
  onResolved,
}: {
  issue: CleanupIssueSummary;
  authz: CleanupAuthz;
  onResolved: () => void;
}) {
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [assigneeId, setAssigneeId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<null | "assign" | "note">(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listAssignableUsers()
      .then((r) => {
        if (!cancelled) setUsers(r.users);
      })
      .catch(() => {
        // Assignee picker is one of two paths; a failed list just
        // leaves "Add a note" available (still no dead end).
        if (!cancelled) setUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function assign() {
    if (!assigneeId) return;
    setBusy("assign");
    setErr(null);
    setMsg(null);
    try {
      await api.assignCleanupIssue(issue.id, assigneeId);
      const who =
        users.find((u) => u.id === assigneeId)?.name ?? "the owner";
      setMsg(`Handed to ${who}. It's in their queue now — not a dead end.`);
      onResolved();
    } catch (e) {
      setErr(
        e instanceof ApiError ? e.message : "Couldn't assign. Try again.",
      );
    } finally {
      setBusy(null);
    }
  }

  async function addNote() {
    const text = note.trim();
    if (text.length === 0) return;
    setBusy("note");
    setErr(null);
    setMsg(null);
    try {
      await api.addCleanupIssueNote(issue.id, text);
      setNote("");
      setMsg("Note added to the thread.");
      onResolved();
    } catch (e) {
      setErr(
        e instanceof ApiError ? e.message : "Couldn't add the note.",
      );
    } finally {
      setBusy(null);
    }
  }

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--blue-dark)",
    marginBottom: 5,
  };
  const inputStyle: React.CSSProperties = {
    width: "100%",
    border: "1px solid var(--mid-gray)",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13,
    color: "var(--navy)",
    background: "var(--bg-0)",
    boxSizing: "border-box",
  };

  return (
    <div>
      <div className="cu-dr-s12">
        View only
      </div>
      <div className="cu-dr-s13">
        <div className="cu-dr-s14">
          {authz.reason}
        </div>
        {authz.pathForward && (
          <div className="cu-dr-s15">
            {authz.pathForward}
          </div>
        )}
      </div>

      {msg && (
        <div className="cu-dr-s16">
          {msg}
        </div>
      )}
      {err && (
        <div className="cu-dr-s17">
          {err}
        </div>
      )}

      <div className="cu-dr-s18">
        <label style={labelStyle} htmlFor="cu-restricted-assign">
          Hand it to the right owner
        </label>
        <select
          id="cu-restricted-assign"
          value={assigneeId}
          onChange={(e) => setAssigneeId(e.target.value)}
          style={{ ...inputStyle, marginBottom: 8 }}
        >
          <option value="">Choose who should own this…</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name ?? u.email}
              {u.role_slug ? ` · ${u.role_slug}` : ""}
            </option>
          ))}
        </select>
        <Button
          variant="sw-primary"
          onClick={assign}
          disabled={!assigneeId || busy !== null}
        >
          {busy === "assign" ? "Assigning…" : "Assign to owner"}
        </Button>
      </div>

      <div>
        <label style={labelStyle} htmlFor="cu-restricted-note">
          …or add a note for whoever picks it up
        </label>
        <textarea
          id="cu-restricted-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="What you noticed / what they should check."
          style={{
            ...inputStyle,
            resize: "vertical",
            font: "inherit",
            marginBottom: 8,
          }}
        />
        <Button
          variant="sw-default"
          onClick={addNote}
          disabled={note.trim().length === 0 || busy !== null}
        >
          {busy === "note" ? "Adding…" : "Add note"}
        </Button>
      </div>
    </div>
  );
}

export function DecideRail({
  issue,
  onResolved,
  onActed,
  onRequestFieldMerge,
  onRequestHouseholdFieldMerge,
  onRequestHouseholdSearchMerge,
  onRequestContactMove,
  onRequestRecordMove,
  onRequestRealtyLink,
  onRequestCreatePolicy,
  onRequestEdit,
}: {
  issue: CleanupIssueSummary | null;
  onResolved: () => void;
  // Audit 6.1 — fired on a successful decide so the page can show a
  // persistent post-action undo strip. Needed because onResolved()
  // refreshes the queue, which moves the selection and unmounts this
  // rail's own done/undo state before the user can see it.
  onActed?: (a: DecideActedEvent) => void;
  // Opens the full per-field cluster merge review (N=2 inline, N≥3
  // pairwise) for the issue's contact cluster. Hosted by the page so
  // it can overlay the whole workspace.
  onRequestFieldMerge: (
    members: RecordRef[],
    matchType: "email" | "phone" | "name_zip",
    matchValue: string,
  ) => void;
  // Baker 2026-05-22: the parallel path for a 2-record HOUSEHOLD
  // merge issue (e.g. mortgage stub-household-merge). Lets Baker pick
  // the survivor AND review every field before the merge runs —
  // restoring the regression he hit when the new detector started
  // emitting household-level merges and the only button was a blind
  // quick-merge with the stub as survivor.
  //
  // Joy 2026-07-02: the couple path ("link as one household" on a
  // contact duplicate) reuses this same overlay, and passes opts so
  // the preselected survivor + merge reason fit that flow instead of
  // the stub-absorption defaults.
  onRequestHouseholdFieldMerge: (
    members: RecordRef[],
    opts?: {
      defaultSurvivorId?: string;
      initialReason?: string;
      // The person has answered "same person under two surnames": the
      // merge review also offers, ticked, to combine the two person cards.
      samePersonConfirmed?: boolean;
    },
  ) => void;
  // Grant 2026-07-08: the search-and-merge counterpart for issues that
  // only know ONE household (the source) — spouse-pass, trust-reattach's
  // review branch. Opens a search UI to find the destination household,
  // rather than the pre-known-pair overlay above. See
  // household-search-merge-predicate.ts.
  onRequestHouseholdSearchMerge?: (source: RecordRef) => void;
  // Grant 2026-07-08: the move counterpart for an unhoused contact
  // (contact-no-household's `move` recommendation) — opens a search UI
  // to find the destination household and move the contact into it. See
  // contact-move-predicate.ts.
  onRequestContactMove?: (source: RecordRef) => void;
  // 2026-09-12 — the RECORD counterpart of the contact move above, and
  // the same dead end: `move-household` is recommended by three detectors
  // and POST /api/cleanup/decide has no case for it, so the recommended
  // button returned 501 `verb_not_wired`. Opens the real picker
  // (MoveRecordHouseholdModal → POST /api/records/:kind/:id/move-household)
  // instead of adding a second copy of the move to the decide switch.
  onRequestRecordMove?: (target: RecordMoveTarget) => void;
  // Nate 2026-07-09: the realty-link counterpart for
  // realty-sr-needs-client-household — opens a household picker to link a
  // synthetic-household realty service record to its real client
  // household (or create a realty-only client). Distinct from the
  // household-merge paths above (this is a `match` on a service_record,
  // not a household merge). See realty-link-predicate.ts.
  onRequestRealtyLink?: (source: RecordRef) => void;
  // Grant 2026-08-18 — "the carrier sent a policy we don't have": opens
  // the picker that creates it (household search, or a new client
  // pre-filled from the carrier's insured). Same shape as the realty
  // link above: the recommendation names the action, the overlay
  // supplies the one thing only a human can — where it goes.
  onRequestCreatePolicy?: (
    source: RecordRef,
    suggested: {
      first: string;
      last: string;
      policyType: string;
      // Pre-selected household when the matcher already identified the
      // family by address; null when nobody matched and the picker
      // opens on search.
      household: { id: string; name: string } | null;
    },
  ) => void;
  // Donna's request: fix the record's core fields (name / email /
  // phone / address) in place, without leaving Cleanup to hunt for
  // "Edit details" on the full record. Opens the record drawer straight
  // into its inline editor. Only offered for a single-record contact /
  // household issue (a duplicate cluster edits each record via its name
  // in the research pane instead). Optional so a host that doesn't wire
  // it simply omits the button.
  onRequestEdit?: (record: RecordRef) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Verb | null>(null);
  const [rationale, setRationale] = useState("");
  // Baker feedback: confirm-tier requires a canonical reason CODE
  // (audit-quality, doctrine) — the backend gates on this and the UI
  // previously had no picker, so dismiss + other confirm-tier actions
  // were a dead end. Empty string = "not yet picked" (gates submit).
  const [reasonCode, setReasonCode] = useState<string>("");
  // Grant 2026-07-08 — the reason picker used to show all 8 codes
  // regardless of verb (e.g. "Deceased" pickable for a household merge).
  // Filtered by the active verb's action class when it's merge/archive;
  // null (→ falls back to the static REASON_CODE_HELP list) for every
  // other verb and on fetch failure — never a harder dead end than
  // before this existed.
  const [reasonCodeOptions, setReasonCodeOptions] = useState<Array<{
    code: string;
    display_label: string;
    description: string;
  }> | null>(null);
  // Baker 2026-05-29 — "Retag as vendor" picker. When the user
  // clicks "Retag as vendor" in the override row we show a small
  // inline picker so they can choose WHICH cluster member is the
  // vendor. The chosen id is sent as params.contact_id to the
  // backend verb.
  const [vendorPickerOpen, setVendorPickerOpen] = useState(false);
  const [vendorPickedId, setVendorPickedId] = useState<string | null>(null);
  // Step 4 — "both are vendors": retag EVERY cluster member, keep them
  // separate (no merge, no single-pick). Sends params.contact_ids.
  const [vendorAllMode, setVendorAllMode] = useState(false);
  // Baker 2026-06-27 — optional vendor classification captured at tag
  // time: what KIND of vendor + which company. "" = unset (omitted from
  // the request, never wipes an existing value). Shared across both the
  // single-pick and "both are vendors" panels.
  const [vendorSubtype, setVendorSubtype] = useState<string>("");
  const [vendorCompanySlug, setVendorCompanySlug] = useState<string>("");
  const [done, setDone] = useState<{
    verb: string;
    undo:
      | { available: true; kind: string; refs: Record<string, unknown> }
      | { available: false };
  } | null>(null);
  // B4 — the actor's authz for this issue, fetched up front so an
  // out-of-scope user sees the no-dead-end panel instead of clicking
  // into a 403. null = unknown (not yet loaded, or the lookup failed):
  // we DON'T block on unknown — POST /decide still hard-enforces as
  // the backstop, so a transient authz failure can't let a forbidden
  // write through.
  const [authz, setAuthz] = useState<CleanupAuthz | null>(null);
  // Audit 6.2 — advisor-gap issues: the steward picks WHO before the
  // assign can run; the button then names them.
  const [advisorId, setAdvisorId] = useState("");
  const [advisorOptions, setAdvisorOptions] = useState<AssignableUser[]>(
    [],
  );
  // Joy 2026-07-01 — insurance expiration issues: the steward enters
  // the carrier-confirmed expiration; "verify" writes it onto the
  // policy (required for ins-missing-expiration, optional refresh for
  // ins-stale-active).
  const [expirationDate, setExpirationDate] = useState("");
  // Nate 2026-07-02 — client-since issues: the steward enters the date
  // the relationship began; "verify" writes it onto the household's
  // company relationship (required — setting it IS the action).
  const [clientSinceDate, setClientSinceDate] = useState("");
  // Grant 2026-07-12 — "earliest I know of": stores the typed date as a
  // FLOOR ("client since at least …", migration 0200) instead of exact.
  const [clientSinceIsFloor, setClientSinceIsFloor] = useState(false);
  // Grant 2026-07-12 — program-type issues: the steward picks the loan
  // program from the full set (Conventional / FHA / VA / USDA / Jumbo /
  // HELOC / Reverse / Non-QM / Other); "verify" writes it onto the loan
  // (required — setting it IS the action). The note is optional, mainly
  // for Other.
  const [programType, setProgramType] = useState("");
  const [programTypeNote, setProgramTypeNote] = useState("");
  // Grant 2026-07-10 QA — missing-effective-date issues: the steward
  // enters the record's start date; "verify" writes it onto the
  // record (required — setting it IS the action).
  const [effectiveDate, setEffectiveDate] = useState("");
  // Grant 2026-07-11 — pm-entity-name-needed: Mike types the business
  // entity's name; "verify" creates it + moves the loans (typing the
  // name IS the action). The escape ("Not known — keep under the
  // personal name") posts keep-current with no params.
  const [entityName, setEntityName] = useState("");
  // Grant 2026-07-13 — marital-status issues: the steward picks the
  // marital status; "verify" writes it onto the primary contact (required
  // — setting it IS the action). The escape ("We don't know their marital
  // status") posts keep-current with no params.
  const [maritalStatus, setMaritalStatus] = useState("");
  // Baker 2026-07-31 — hh-no-primary: the steward picks WHICH member is
  // the primary; "verify" writes the pointer and demotes every other
  // claimant (picking IS the action, so the backend 400s without it).
  // Candidates are the household's live members, loaded from the record
  // itself — the issue's affectedRecords carries only the household.
  const [primaryPick, setPrimaryPick] = useState("");
  const [primaryCandidates, setPrimaryCandidates] = useState<
    PrimaryCandidate[]
  >([]);
  const [primaryLoading, setPrimaryLoading] = useState(false);
  const [primaryLoadErr, setPrimaryLoadErr] = useState<string | null>(null);
  // Baker 2026-07-13 — pre-2018 PM revenue-gap issues: Baker types the
  // amount Stewardship earned (dollars); "verify" writes it onto the loan
  // (required — setting it IS the action). The escape ("We can't
  // determine this fee") posts keep-current with no params.
  const [feeAmount, setFeeAmount] = useState("");
  // Baker 2026-07-31 — realty-zero-price-transaction: the typed sale
  // price IS the action (the backend 400s without it).
  const [salePrice, setSalePrice] = useState("");
  // P1-5 (cleanup UX audit, 2026-07-13) — insurance-duplicate-policy
  // side-by-side rows: the steward can click "Keep this one" on a row
  // to override which policy survives the merge (null = use the
  // detector's recommended default).
  const [dupPolicySurvivorId, setDupPolicySurvivorId] = useState<
    string | null
  >(null);
  // P1-6 (cleanup UX audit, 2026-07-13) — contact-no-contact-method:
  // the steward types an email and/or phone inline; "verify" writes
  // whichever is filled onto the contact (at least one IS the action
  // — the escape below stays live for "genuinely neither").
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  // Follow-up parking (Donna's request) — the inline "set this aside
  // while I wait on an answer" form, in the override row.
  const [followupOpen, setFollowupOpen] = useState(false);
  const [followupReason, setFollowupReason] = useState("");
  // Joy 2026-07-02 — "link as one household" (the couple answer on a
  // duplicate-contact issue). Busy while the two households are looked
  // up; the note explains the already-linked / no-household / stale
  // outcomes inline instead of dead-ending.
  const [linkHhBusy, setLinkHhBusy] = useState(false);
  const [linkHhNote, setLinkHhNote] = useState<{
    tone: "info" | "warn";
    text: string;
  } | null>(null);
  // Grant 2026-08-10 — a couple already linked in one household (the
  // detector's flag text says so) must not ALSO be offered a "link as
  // one household…" button that implies clicking might do something:
  // that combination read as contradictory and scared the steward off
  // clicking either. Quietly pre-check the household state when a
  // couple-shaped card opens (no busy spinner, no error surface — the
  // click handler below re-verifies live and is the real source of
  // truth) so the button can say "keep both (already one household)"
  // up front instead of only after clicking.
  const [linkHhPlan, setLinkHhPlan] = useState<LinkHouseholdPlan | null>(
    null,
  );
  // Donna's request (2026-07-14) — "Assign to a teammate…" from the
  // in-record drawer. Parity with the centralized workspace, which could
  // already hand an issue to another employee; the drawer could not, so a
  // front-desk user researching a record had no way to route the item to
  // (say) Brandon without leaving. Reuses the SAME mechanism the
  // RestrictedPanel + workspace use: POST /api/cleanup/issues/:id/assign
  // via api.assignCleanupIssue, with the shared assignable-users list.
  // `teammates` is loaded once on mount (the roster is stable across the
  // selected issue) so it can both populate the picker AND resolve the
  // current-assignee name. The rest is per-issue and resets on selection.
  const [teammates, setTeammates] = useState<AssignableUser[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignPickedId, setAssignPickedId] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignMsg, setAssignMsg] = useState<string | null>(null);

  const issueId = issue?.id ?? null;
  const advisorGapIssue = issue != null && isAdvisorGapIssue(issue);
  const expirationIssue = issue != null && isExpirationIssue(issue);
  const clientSinceIssue = issue != null && isClientSinceIssue(issue);
  const effectiveDateIssue = issue != null && isEffectiveDateIssue(issue);
  const pmEntityIssue = issue != null && isPmEntityIssue(issue);
  const programTypeIssue = issue != null && isProgramTypeIssue(issue);
  const maritalIssue = issue != null && isMaritalIssue(issue);
  const primaryIssue = issue != null && isPrimaryIssue(issue);
  const pmFeeIssue = issue != null && isPmFeeIssue(issue);
  const realtyPriceIssue = issue != null && isRealtyPriceIssue(issue);
  // Baker 2026-07-30 — commission-pending-stale's recommendation names
  // "the commission flow" but nothing on the screen linked to it, and
  // the subject (a commission_reports row) has no record page, so the
  // whole issue was a dead end. Renders a real link into
  // /admin/commissions + an honestly-labeled resolve button.
  const commissionIssue = issue != null && isCommissionIssue(issue);
  const quarantineIssue = issue != null && isQuarantineIssue(issue);
  const vendorLookalikeIssue =
    issue != null && isVendorLookalikeIssue(issue);
  // Donna 2026-07-30 — combined-contact + hh-empty archived-member get
  // dedicated two-answer blocks (their old "review" recommendations
  // pointed at affordances that don't exist on the record pages).
  const combinedContactIssue =
    issue != null && isCombinedContactIssue(issue);
  const archivedMemberIssue =
    issue != null && isArchivedMemberIssue(issue);
  // Grant-approved 2026-07-14 — the "stop asking" escapes for
  // contact-no-contact-method and mortgage-missing-funded-date render as
  // standalone escape blocks; sr-missing-effective-date's escape lives
  // inside the existing effective-date verify block (effectiveDateIssue).
  const noContactMethodIssue =
    issue != null && isNoContactMethodIssue(issue);
  const fundedDateUnknownIssue =
    issue != null && isFundedDateUnknownIssue(issue);
  // Nate 2026-07-14 — realty-pending's "still an active listing, not under
  // contract yet" escape renders as a standalone block (like funded-date).
  const realtyStillActiveIssue =
    issue != null && isRealtyStillActiveIssue(issue);

  useEffect(() => {
    if (!advisorGapIssue) return;
    let cancelled = false;
    // Only the record's company's own advisors (2026-10-02); the server
    // refuses anyone else.
    const company = issue?.owningCompanySlug ?? null;
    (company ? api.listCompanyAdvisors(company) : api.listAssignableUsers())
      .then((r) => {
        if (!cancelled) setAdvisorOptions(r.users);
      })
      .catch(() => {
        if (!cancelled) setAdvisorOptions([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- issue changes with issueId
  }, [advisorGapIssue, issueId]);
  // Donna 2026-07-14 — the teammate roster for "Assign to a teammate…".
  // Loaded once on mount (stable across the selected issue); a failed
  // fetch just leaves an empty list, so the button explains it and the
  // other actions stay available (no dead end).
  useEffect(() => {
    let cancelled = false;
    api
      .listAssignableUsers()
      .then((r) => {
        if (!cancelled) setTeammates(r.users);
      })
      .catch(() => {
        if (!cancelled) setTeammates([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    // Baker 2026-05-29 — reset ALL per-issue local state when the
    // selected issue changes. Previously only `authz` reset here, so
    // after a dismiss/merge set `done`, navigating to the next issue
    // (or clicking away and back) left the rail frozen on the
    // "Done — dismiss" success screen. The only way to clear it was a
    // full reload (which remounts + re-inits state). Resetting on
    // issueId change makes the rail track the selected issue.
    setDone(null);
    setChosen(null);
    setRationale("");
    setReasonCode("");
    setVendorPickerOpen(false);
    setVendorPickedId(null);
    setVendorAllMode(false);
    setAdvisorId("");
    setExpirationDate("");
    setClientSinceDate("");
    setClientSinceIsFloor(false);
    setEffectiveDate("");
    setProgramType("");
    setProgramTypeNote("");
    setEntityName("");
    setMaritalStatus("");
    setPrimaryPick("");
    setPrimaryCandidates([]);
    setPrimaryLoading(false);
    setPrimaryLoadErr(null);
    setFeeAmount("");
    setSalePrice("");
    setDupPolicySurvivorId(null);
    setContactEmail("");
    setContactPhone("");
    setFollowupOpen(false);
    setFollowupReason("");
    setLinkHhBusy(false);
    setLinkHhNote(null);
    setLinkHhPlan(null);
    setAssignOpen(false);
    setAssignPickedId("");
    setAssignBusy(false);
    setAssignMsg(null);
    setErr(null);
    if (!issueId) {
      setAuthz(null);
      return;
    }
    let cancelled = false;
    setAuthz(null);
    api
      .getCleanupIssueAuthz(issueId)
      .then((r) => {
        if (!cancelled) setAuthz(r.authz);
      })
      .catch(() => {
        if (!cancelled) setAuthz(null);
      });
    return () => {
      cancelled = true;
    };
  }, [issueId]);
  // Baker 2026-07-31 — load the household's live members so the rail can
  // render the primary picker. The issue's affectedRecords carries only
  // the household, and the member list is exactly what the steward needs
  // to see to answer "which one?" — so it's fetched here rather than
  // being denormalized onto the issue (which would go stale).
  const primaryHouseholdId =
    issue != null && isPrimaryIssue(issue)
      ? (issue.affectedRecords.find((r) => r.kind === "household")?.id ?? null)
      : null;
  useEffect(() => {
    if (!primaryHouseholdId) return;
    let cancelled = false;
    setPrimaryLoading(true);
    setPrimaryLoadErr(null);
    api
      .getHousehold(primaryHouseholdId)
      .then((detail) => {
        if (cancelled) return;
        const pointerId = detail.household.primary_contact_id;
        // No client-side eligibility filter here on purpose: the
        // household detail endpoint's own query already returns exactly
        // the live, unarchived, person members (listContactsByHousehold,
        // called without includeArchived) — which is the same rule
        // setHouseholdPrimaryContact enforces on the write. Re-asserting
        // it here would mean filtering on fields HouseholdContact doesn't
        // declare, and two copies of one rule is how they drift apart.
        const members: PrimaryCandidate[] = detail.contacts
          .map((m) => ({
            id: m.id,
            name:
              displayLabel(
                (
                  m.preferred_name ??
                  [m.legal_first_name, m.legal_last_name]
                    .filter(Boolean)
                    .join(" ")
                ).trim(),
              ) || "(unnamed contact)",
            isPointer: m.id === pointerId,
            claimsPrimary:
              (m.household_role ?? "").toLowerCase() === "primary",
          }));
        setPrimaryCandidates(members);
        // Pre-select the member the household already points at — the
        // multi-primary case is usually "this pointer is right, demote
        // the other one", so the common answer is one click. Never
        // pre-selects when there is no pointer: that IS the open
        // question, and a default would put words in the steward's mouth.
        const pointer = members.find((m) => m.isPointer);
        setPrimaryPick(pointer ? pointer.id : "");
      })
      .catch(() => {
        if (cancelled) return;
        setPrimaryCandidates([]);
        setPrimaryLoadErr(
          "Couldn't load this household's members. Open the household record to set the primary there.",
        );
      })
      .finally(() => {
        if (!cancelled) setPrimaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [primaryHouseholdId]);
  // The couple-shaped pair this rail would offer to link, if any — computed
  // independently of the `cluster` const further down (which is declared
  // after this component's early `if (!issue) return` and so isn't
  // available yet here; every hook must run unconditionally on every
  // render, so this has to sit before that return). Recomputed each
  // render (cheap: two array filters), but only its two-contact-id STRING
  // feeds the effect below, so the fetch itself doesn't refire every
  // render — only when the actual pair changes.
  const linkHouseholdPair =
    issue && canOfferLinkHousehold(issue) ? contactClusterRefs(issue) : null;
  const linkHouseholdCandidateKey =
    linkHouseholdPair && linkHouseholdPair.length === 2
      ? `${linkHouseholdPair[0]!.id}:${linkHouseholdPair[1]!.id}`
      : null;
  // Grant 2026-08-10 — quietly pre-check whether a couple-shaped
  // duplicate's two contacts are already in one household, so the "link
  // as one household…" button can say "keep both (already one
  // household)" up front instead of only after clicking (see the
  // linkHhPlan state comment above). Errors are swallowed on purpose:
  // the click handler (linkAsHousehold) re-fetches and surfaces any real
  // problem then — this pass is a hint, not the source of truth.
  useEffect(() => {
    if (!linkHouseholdCandidateKey || !linkHouseholdPair) return;
    const [pa, pb] = linkHouseholdPair;
    let cancelled = false;
    void (async () => {
      try {
        const [ra, rb] = await Promise.all([
          api.getContactDetail(pa!.id),
          api.getContactDetail(pb!.id),
        ]);
        if (cancelled) return;
        setLinkHhPlan(
          planLinkHouseholds(
            {
              contactLabel: displayLabel(pa!.label ?? "") || "the first contact",
              household: ra.household,
            },
            {
              contactLabel: displayLabel(pb!.label ?? "") || "the other contact",
              household: rb.household,
            },
          ),
        );
      } catch {
        // Leave linkHhPlan null — the button falls back to its default
        // label, and clicking it surfaces the real error.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on
    // the stable id-pair string, not the freshly-filtered array.
  }, [linkHouseholdCandidateKey]);
  useEffect(() => {
    // Grant 2026-07-08 — fetch verb-filtered reason codes. Computed
    // independently of `issue`/`chosen` derivations further down (this
    // effect sits before the early `if (!issue) return null`, so it
    // must not depend on values computed after it — Rules of Hooks).
    if (!issue) {
      setReasonCodeOptions(null);
      return;
    }
    const verb = chosen ?? applicability(issue).verb;
    const actionClass =
      verb === "merge"
        ? "merge"
        : verb === "archive" || verb === "archive-empty-household"
          ? "archive"
          : null;
    if (!actionClass) {
      setReasonCodeOptions(null);
      return;
    }
    let cancelled = false;
    api
      .listChangeReasonCodes(actionClass)
      .then((r) => {
        if (!cancelled) setReasonCodeOptions(r.codes);
      })
      .catch(() => {
        if (!cancelled) setReasonCodeOptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, [issueId, chosen]);

  if (!issue) {
    return (
      <Rail>
        <div className="cu-dr-s19">
          Pick an issue to see the recommended action.
        </div>
      </Rail>
    );
  }

  // Audit 6.1 — an already-resolved issue (the "Recently resolved"
  // lane) gets the Resolved panel + permanent Reopen, never the
  // normal action buttons. `done` wins so the fresh-decision undo
  // screen still shows right after acting.
  if (!done && TERMINAL_STATES.has(issue.state)) {
    return (
      <Rail>
        <ResolvedPanel issue={issue} onResolved={onResolved} />
      </Rail>
    );
  }

  // Parked in the "Waiting on follow-up" lane → the dedicated panel
  // (bring it back to act on it), never the normal action buttons.
  if (!done && issue.state === "snoozed" && issue.followupAt) {
    return (
      <Rail>
        <FollowupPanel issue={issue} onResolved={onResolved} />
      </Rail>
    );
  }

  // Donna 2026-07-23 — unmatched import rows get the dedicated resolver
  // (live candidate context + one-click merge/skip). Before this, the
  // recommended action pointed at a review screen that no longer exists
  // — the exact dead end UX rule #5 forbids.
  if (!done && issue.detectorSlug === "hh-match-quarantine") {
    return (
      <Rail>
        <QuarantineMatchPanel issue={issue} onResolved={onResolved} />
      </Rail>
    );
  }

  // B4 — out-of-scope: show the boundary + forward paths, never the
  // action buttons (they'd 403). Research stays fully available.
  if (authz && !authz.allowed) {
    return (
      <Rail>
        <RestrictedPanel
          issue={issue}
          authz={authz}
          onResolved={onResolved}
        />
      </Rail>
    );
  }

  // Document Compliance Program — a document past its retention date
  // gets the disposition panel instead of the generic verbs. Its three
  // answers (keep longer / legal hold / destroy) act on the DOCUMENT,
  // not on the issue, so they call purpose-built admin-gated endpoints
  // rather than teaching /api/cleanup/decide how to delete files.
  // Placed AFTER the authz check on purpose: a user who can't act on
  // this issue keeps getting the honest RestrictedPanel, never a panel
  // of buttons that would 403.
  if (!done && issue.detectorSlug === "retention-due") {
    return (
      <Rail>
        <RetentionDispositionPanel issue={issue} onResolved={onResolved} />
      </Rail>
    );
  }

  const app = applicability(issue);
  // Donna 2026-07-14 — the teammate this issue is currently assigned to
  // (if any), resolved to a name for the "Assigned to …" indicator. The
  // roster may not have loaded yet (or the assignee left the assignable
  // set); fall back to a generic label rather than showing a raw id.
  const currentAssignee = issue.assignedOwnerUserId
    ? (teammates.find((u) => u.id === issue.assignedOwnerUserId) ?? null)
    : null;
  // Any 2+-contact duplicate cluster → offer the full per-field merge
  // review (the user's explicit ask: "click various fields to choose
  // what stays and goes"). The orchestrator does N=2 inline and N≥3
  // pairwise, so a 3-record cluster is no longer a dead end.
  const cluster = contactClusterRefs(issue);
  // Baker 2026-05-22: parallel path for HOUSEHOLD-level merge issues.
  // When present, the "Review every field, then merge…" button below
  // opens HouseholdClusterMergeOverlay (survivor pick + per-field
  // review). We also suppress the blind quick-merge button on this
  // path — for the stub detector the quick-merge picks recs[0] as
  // survivor, which is the STUB (wrong direction). The user MUST go
  // through the overlay so they pick + review.
  const householdCluster = householdClusterRefs(issue);
  // Grant 2026-07-25 (Donna's Needham/Needam report) — true when
  // householdCluster is only present because of the "different surname,
  // same address" split-family case, which carries NO recommendation
  // (the human must confirm it's actually one family — Grant's
  // "auto-merge nothing" call). Swaps the button copy below from
  // "the recommended survivor is pre-selected" (false here — there's no
  // recommendation) to an honest "you pick who survives" explanation.
  const householdClusterUnconfirmed =
    householdCluster != null && isUnconfirmedSplitFamily(issue);
  // An import placeholder beside the family its loan points at. Gets its
  // own three answers below instead of the generic merge button, because
  // "merge" is only one of three things the steward might have concluded
  // and the other two had nowhere to go (Grant 2026-09-15).
  const stubWithCandidate =
    householdCluster != null && isImportStubWithCandidate(issue);
  // Grant 2026-07-08 — issues that only know ONE household (spouse-pass,
  // trust-reattach's review branch): the destination has to be found by
  // search, so this renders a different button than householdCluster
  // above (which already has both households). Suppressed whenever
  // householdCluster already fires (that 2-known-households shape wins).
  const searchMergeSource = householdCluster
    ? null
    : householdSearchMergeSource(issue);
  // Cambria 2026-07-31 — one mortgage-import stub facing N identically-
  // named real families ("Which real household is Malcolm Fleming?").
  // She had worked out the answer and had nowhere to put it: the issue
  // carries no recommendation by design, and the two existing merge
  // affordances above both miss this shape (one needs exactly two known
  // households, the other needs none). Each row below opens the same
  // per-field merge review with that family pre-selected as survivor.
  const twinPick = stubTwinChoices(issue);
  // Donna 2026-07-30 — the NO half of the single-member-household
  // question. Only meaningful alongside the search-merge (YES) button, so
  // the two answers render as a pair; see lives-alone-answer.ts.
  const livesAlone = issue != null ? livesAloneAnswer(issue) : null;
  // Whether the No button actually renders — it lives inside the
  // search-merge block, so it inherits that block's conditions. Used below
  // to drop the generic duplicate-family "Different people — keep
  // separate" button, which would otherwise sit under "Or do something
  // else" as a second, worse-worded No for the same question.
  const livesAloneShown =
    livesAlone != null &&
    searchMergeSource != null &&
    onRequestHouseholdSearchMerge != null &&
    !quarantineIssue;
  // Ryan 2026-08-25 — the third answer to the same question: the partner
  // is real but isn't in the system yet, so neither "find their
  // household" (nothing to find) nor "on their own" (they aren't) fits.
  // Opens the household's existing Add Member flow in a new tab; see
  // add-household-member-answer.ts.
  const addMember = issue != null ? addHouseholdMemberAnswer(issue) : null;
  const addMemberShown =
    addMember != null && searchMergeSource != null && !quarantineIssue;
  // Andrea 2026-08-27 — the fourth answer: the spouse is real and
  // known, but will NEVER become a Stewardship contact. Neither
  // "lives alone" (false — there is a spouse) nor "add a household
  // member" (implies she'll eventually be entered) fit; see
  // spouse-not-a-client-answer.ts.
  const spouseNotAClient = issue != null ? spouseNotAClientAnswer(issue) : null;
  const spouseNotAClientShown =
    spouseNotAClient != null && searchMergeSource != null && !quarantineIssue;
  // Grant 2026-07-08 — contact-no-household's `move` recommendation:
  // find a household and move the contact into it.
  const contactMoveTarget = contactMoveSource(issue);
  const recordMoveNeeded = recordMoveTarget(issue);
  // The other answer on a "should this loan move?" card: the two
  // households are one person written twice (Grant, 2026-09-16).
  const loanMergeAlternative = loanHouseholdMergeAlternative(issue);
  // Nate 2026-07-09 — realty-sr-needs-client-household: the service
  // record must be linked to its real client household via a picker.
  // Its recommendation verb is `match` (in RECON_VERBS), so applicability
  // would otherwise mark it one-click-applicable with NO params — which
  // is a guaranteed 400 target_required (linking IS the action). Suppress
  // the generic Apply and render the picker button instead.
  const realtyLinkTarget = realtyLinkSource(issue);
  const createPolicyTarget = ivansCreatePolicySource(issue);
  // Donna's inline-edit affordance target: a single contact / household
  // this issue is about. Suppressed for multi-record clusters (those edit
  // each record via its name in the research pane, which opens the same
  // drawer editor) so the button is never ambiguous. Not a vendor: a
  // cleanup vendor ref is a row in the vendors list, which the drawer's
  // contact editor can't load (it opened onto a blank editor); vendors
  // are edited on their own page (2026-10-02).
  const editableAffected = issue.affectedRecords.filter(
    (r) => r.kind === "contact" || r.kind === "household",
  );
  const singleEditable =
    editableAffected.length === 1 ? editableAffected[0]! : null;
  const recVerb = (issue.recommendation?.verb ?? "") as string;
  // The verb we'd apply: the user's override choice, else recommended.
  const activeVerb: Verb = chosen ?? app.verb;
  // The steward picked a disposition from "or do something else".
  const hasChoice = chosen != null;
  // An "override" is choosing something OTHER than a genuine
  // recommendation (UX rule #6 — needs a one-line rationale). Keyed off
  // `issue.recommendation`, NOT `app.verb`: when there's no
  // recommendation (a couple, not a duplicate), `app.verb` falls back to
  // "dismiss", so the old `chosen !== app.verb` test made "keep
  // separate" look like a non-override AND collide with the fallback —
  // leaving no Apply button at all (Baker's dead end). Now "keep
  // separate" on a no-recommendation issue is simply the answer, not an
  // override, and always renders its own Apply button below. — Baker 2026-06-26
  const isOverride =
    hasChoice &&
    issue.recommendation != null &&
    chosen !== issue.recommendation.verb;
  // confirm-tier always needs a reason code (Baker fix — backend
  // gates on this); standard-tier doesn't.
  //
  // MIRRORS the server's REASON_EXEMPT_VERBS in
  // workers/api/src/routes/cleanup.ts — verbs that change no record have
  // no blast radius for a code to explain, so requiring one only strands
  // the steward in front of a dropdown of merge/archive/delete reasons
  // that don't describe what they're doing (Grant, 2026-08-10, answering
  // "A couple — keep both"). Keep the two lists in step: the server is
  // the enforcement, this is the affordance.
  const REASON_EXEMPT_VERBS = new Set<string>([
    "keep-current",
    "skip-import-row",
    "dismiss",
    "snooze",
  ]);
  const needsReasonCode =
    issue.tier === "confirm" &&
    reasonCode === "" &&
    !REASON_EXEMPT_VERBS.has(activeVerb);
  // override always needs a one-line rationale (UX rule #6); the
  // "Other — type a reason" code also needs the text.
  const needsRationale =
    (isOverride || reasonCode === "other_with_text") &&
    rationale.trim().length === 0;
  // Cambria 2026-07-31, second half of the same report: "i only see the
  // reason for this action and when i pick the reason it doesnt let me
  // save it or anything." On the twin-pick path the reason box rendered
  // on its own, above the fold, labelled "required (type-to-confirm)",
  // with no Save button anywhere near it and no action it could apply
  // to — both real answers there carry their own reason (the merge
  // collects one in the review overlay; "none of these" sends its own
  // code). A required-looking field that nothing consumes reads as a
  // broken form. It comes back the moment she picks a disposition from
  // "or do something else" (which brings its own Apply button), and on
  // an override, which always needs it.
  //
  // Scoped to twinPick ONLY, deliberately. The other overlay-resolved
  // paths (householdCluster, searchMergeSource) render alongside
  // dedicated buttons that call act() directly — hh-quarantined's
  // "archive it" is confirm-tier and reads the code out of THIS picker,
  // so hiding it there would trade one dead end for another.
  const reasonHandledByOverlay = twinPick != null;
  // Surface the picker whenever any path leads to needing a code or
  // an override rationale.
  const showReasonInputs =
    (issue.tier === "confirm" || isOverride) &&
    (hasChoice || !reasonHandledByOverlay);
  // Normalized to REASON_CODE_HELP's {code,label,when} shape so the
  // render below doesn't care which source it came from.
  const visibleReasonCodes = reasonCodeOptions
    ? reasonCodeOptions.map((r) => ({
        code: r.code,
        label: r.display_label,
        when: r.description,
      }))
    : REASON_CODE_HELP;
  // After a failed submit, highlight the field the backend mentioned.
  const errCode = err?.toLowerCase() ?? "";
  const errHighlightsCode =
    errCode.includes("reason code") || errCode.includes("reason_code");
  const errHighlightsText =
    errCode.includes("rationale") ||
    errCode.includes("reason_text") ||
    errCode.includes("one-line reason");

  async function act(
    verb: Verb,
    override: boolean,
    // pm-entity keep-current carries WHICH terminal answer this is
    // (2026-07-23): 'not_known' vs 'individual_business_purpose'.
    keepReason?: PmEntityKeepReason,
    // One-click answers that supply their own rationale (the "No — they're
    // on their own" button). Passed explicitly rather than via
    // setRationale() because that state wouldn't be readable in this same
    // tick, and the audit row would land with no reason text.
    reasonTextOverride?: string,
    // Same idea for the canonical reason CODE. A confirm-tier issue is
    // 400'd server-side without one, so a one-click answer on a
    // confirm-tier issue has to carry its own code rather than rely on
    // the picker's state — which is exactly the picker this path
    // suppresses (Cambria 2026-07-31, the twin picker's "none of these"
    // answer).
    reasonCodeOverride?: string,
  ) {
    setBusy(true);
    setErr(null);
    try {
      const body: Parameters<typeof api.decideCleanupIssue>[0] = {
        issue_id: issue!.id,
        verb,
        overrode_recommendation: override,
      };
      if (verb === "keep-current" && isPmEntityIssue(issue!) && keepReason) {
        body.params = { ...(body.params ?? {}), keep_reason: keepReason };
      }
      if (
        (verb === "merge" || verb === "resolve-trust-reattach") &&
        app.ok &&
        app.params
      ) {
        body.params = app.params;
      }
      // P1-5 — Joy picked a different "Keep this one" row than the
      // detector's recommended default: override the survivor/merged
      // set the generic applicability() computed above. Guarded on the
      // id actually being one of this issue's own affected records —
      // the same anti-spoof shape the backend independently re-checks.
      if (
        verb === "merge" &&
        isDuplicatePolicyIssue(issue!) &&
        dupPolicySurvivorId &&
        issue!.affectedRecords.some((r) => r.id === dupPolicySurvivorId)
      ) {
        body.params = {
          survivor_id: dupPolicySurvivorId,
          merged_ids: issue!.affectedRecords
            .map((r) => r.id)
            .filter((id) => id !== dupPolicySurvivorId),
        };
      }
      if (verb === "snooze") {
        body.params = {
          until: new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
        };
      }
      if (verb === "retag-as-vendor") {
        if (vendorAllMode) {
          // Both (all) cluster members are vendors → retag them all.
          body.params = {
            contact_ids: issue!.affectedRecords
              .filter((r) => r.kind === "contact")
              .map((r) => r.id),
          };
        } else if (vendorPickedId) {
          body.params = { contact_id: vendorPickedId };
        } else {
          // Baker 2026-07-13 — single-contact issues (vendor-lookalike's
          // "It's a business" button): there's no picker because there's
          // nothing to pick — the one contact IS the target.
          const contactRecs = issue!.affectedRecords.filter(
            (r) => r.kind === "contact",
          );
          if (contactRecs.length === 1) {
            body.params = { contact_id: contactRecs[0]!.id };
          }
        }
        // Optional classification — only sent when picked, so leaving the
        // dropdowns blank keeps the fast path one click.
        if (body.params && vendorSubtype) {
          body.params.vendor_subtype = vendorSubtype;
        }
        if (body.params && vendorCompanySlug) {
          body.params.vendor_company_slug = vendorCompanySlug;
        }
      }
      // Audit 6.2 — the advisor-gap assign carries WHO; the backend
      // writes them onto the service record (and 400s without it).
      if (verb === "match" && isAdvisorGapIssue(issue!)) {
        body.params = {
          ...(issue!.recommendation?.params ?? {}),
          advisor_user_id: advisorId,
        };
      }
      // Joy 2026-07-01 — the expiration verify carries the DATE; the
      // backend writes it onto the policy (and 400s without it on
      // ins-missing-expiration).
      if (
        verb === "verify" &&
        isExpirationIssue(issue!) &&
        expirationDate.trim()
      ) {
        body.params = {
          ...(issue!.recommendation?.params ?? {}),
          expiration_date: expirationDate.trim(),
        };
      }
      // Grant 2026-07-11 — the pm-entity verify carries the typed entity
      // NAME; the backend creates the entity + attaches the loans (and
      // 400s without it). keep-current needs no params for this slug.
      if (verb === "verify" && isPmEntityIssue(issue!) && entityName.trim()) {
        body.params = {
          ...(issue!.recommendation?.params ?? {}),
          entity_name: entityName.trim(),
        };
      }
      // Nate 2026-07-02 — the client-since verify carries the DATE; the
      // backend writes it onto the household's company relationship
      // (and 400s without it). Grant 2026-07-12: plus the "earliest I
      // know of" floor flag when ticked.
      if (
        verb === "verify" &&
        isClientSinceIssue(issue!) &&
        clientSinceDate.trim()
      ) {
        body.params = {
          ...(issue!.recommendation?.params ?? {}),
          client_since_date: clientSinceDate.trim(),
          ...(clientSinceIsFloor ? { client_since_is_floor: true } : {}),
        };
      }
      // Grant 2026-07-12 — the program-type verify carries the picked
      // PROGRAM (+ optional note); the backend writes it onto the loan
      // (and 400s without it).
      if (
        verb === "verify" &&
        isProgramTypeIssue(issue!) &&
        programType.trim()
      ) {
        body.params = {
          ...(issue!.recommendation?.params ?? {}),
          loan_program_type: programType.trim(),
          ...(programTypeNote.trim()
            ? { loan_program_type_note: programTypeNote.trim() }
            : {}),
        };
      }
      // P1-6 — the contact-method verify carries whichever of email/
      // phone the steward typed; the backend writes it onto the contact
      // (and 400s if BOTH are blank — that's what the "keep record
      // anyway" escape below is for).
      if (
        verb === "verify" &&
        isNoContactMethodIssue(issue!) &&
        (contactEmail.trim() || contactPhone.trim())
      ) {
        body.params = {
          ...(contactEmail.trim() ? { email: contactEmail.trim() } : {}),
          ...(contactPhone.trim()
            ? { phone_primary: contactPhone.trim() }
            : {}),
        };
      }
      // Baker 2026-07-13 — the pm-fee verify carries the AMOUNT (cents);
      // the backend writes it onto the loan (and 400s without it).
      // keep-current ("We can't determine this fee") needs no params.
      if (verb === "verify" && isPmFeeIssue(issue!) && feeAmount.trim()) {
        const cents = feeInputToCents(feeAmount);
        if (cents !== null) {
          body.params = {
            ...(issue!.recommendation?.params ?? {}),
            amount_earned_cents: cents,
          };
        }
      }
      // Baker 2026-07-31 — the realty zero-price verify carries the SALE
      // PRICE in dollars; the backend writes it onto the property record
      // (and 400s without it).
      if (
        verb === "verify" &&
        isRealtyPriceIssue(issue!) &&
        salePrice.trim()
      ) {
        const price = parseSalePriceInput(salePrice);
        if (price !== null) {
          body.params = {
            ...(issue!.recommendation?.params ?? {}),
            sale_price: price,
          };
        }
      }
      // Baker 2026-07-31 — the hh-no-primary verify carries the picked
      // MEMBER; the backend sets the pointer, demotes every other
      // claimant, and 400s without it. keep-current ("We don't know who
      // the primary contact is") needs no params for this slug.
      if (
        verb === "verify" &&
        isPrimaryIssue(issue!) &&
        isValidPrimaryPick(primaryPick, primaryCandidates)
      ) {
        body.params = {
          ...(issue!.recommendation?.params ?? {}),
          primary_contact_id: primaryPick.trim(),
        };
      }
      // Grant 2026-07-13 — the marital verify carries the picked STATUS;
      // the backend writes it onto the primary contact (and 400s without
      // it). keep-current needs no params for this slug.
      if (
        verb === "verify" &&
        isMaritalIssue(issue!) &&
        maritalStatus.trim()
      ) {
        body.params = {
          ...(issue!.recommendation?.params ?? {}),
          marital_status: maritalStatus.trim(),
        };
      }
      // Grant 2026-07-10 QA — the effective-date verify carries the
      // DATE; the backend writes it onto the record (and 400s without
      // it).
      if (
        verb === "verify" &&
        isEffectiveDateIssue(issue!) &&
        effectiveDate.trim()
      ) {
        body.params = {
          ...(issue!.recommendation?.params ?? {}),
          effective_date: effectiveDate.trim(),
        };
      }
      const reasonText = (reasonTextOverride ?? rationale).trim();
      if (reasonText) body.reason_text = reasonText;
      // Canonical reason code (required by the backend for
      // confirm-tier; helpful audit signal otherwise).
      const code = reasonCodeOverride ?? reasonCode;
      if (code) body.reason_code = code;
      const res = await api.decideCleanupIssue(body);
      setDone({ verb: res.verb, undo: res.undo });
      onActed?.({
        issueId: issue!.id,
        verb: res.verb,
        summary: actionSummary(
          verb,
          issue!,
          advisorOptions.find((u) => u.id === advisorId)?.name ?? null,
        ),
        undo: res.undo,
      });
      onResolved();
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? e.message
          : "Couldn't apply that action. Nothing was changed.",
      );
    } finally {
      setBusy(false);
    }
  }

  // Park the issue for follow-up (Donna's request). Not a /decide verb
  // — it touches no records — so it goes through the dedicated endpoint
  // and just refreshes the queue (the item leaves the active lane).
  async function park() {
    setBusy(true);
    setErr(null);
    try {
      const reason = followupReason.trim();
      await api.parkCleanupIssueFollowup(issue!.id, {
        ...(reason ? { reason } : {}),
      });
      setFollowupOpen(false);
      setFollowupReason("");
      onResolved();
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? e.message
          : "Couldn't set it aside. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  // Donna 2026-07-14 — hand this issue to another employee, from the
  // in-record drawer. Reuses the SAME endpoint the workspace + the
  // no-dead-end RestrictedPanel use (POST /issues/:id/assign). Assigning
  // does NOT resolve the issue — it stays open and moves into the
  // teammate's Cleanup queue. onResolved() refetches so the drawer
  // reflects the new owner immediately; the success line persists (the
  // selected issue is unchanged, so per-issue state isn't reset).
  async function assignToTeammate() {
    const uid = assignPickedId;
    if (!uid) return;
    setAssignBusy(true);
    setErr(null);
    setAssignMsg(null);
    try {
      await api.assignCleanupIssue(issue!.id, uid);
      const picked = teammates.find((u) => u.id === uid);
      const who = picked?.name ?? picked?.email ?? "the teammate";
      setAssignMsg(`Assigned to ${who}. It's in their Cleanup queue now.`);
      setAssignOpen(false);
      onResolved();
    } catch (e) {
      setErr(
        e instanceof ApiError ? e.message : "Couldn't assign. Try again.",
      );
    } finally {
      setAssignBusy(false);
    }
  }

  // Joy 2026-07-02 — the couple answer, wired. Looks up both flagged
  // contacts' households, then either (a) opens the existing household
  // merge overlay pre-filled with the two households (both PEOPLE stay
  // separate contacts — only their households combine; the host marks
  // this issue resolved when the merge lands), (b) says they're
  // already one household (keep-separate is pre-filled as the answer),
  // or (c) names the contact with no household to link (fix path
  // stated — never a dead end).
  async function linkAsHousehold() {
    if (!cluster || cluster.length !== 2) return;
    setLinkHhBusy(true);
    setErr(null);
    setLinkHhNote(null);
    try {
      const [ra, rb] = await Promise.all([
        api.getContactDetail(cluster[0]!.id),
        api.getContactDetail(cluster[1]!.id),
      ]);
      const plan = planLinkHouseholds(
        {
          contactLabel:
            displayLabel(cluster[0]!.label ?? "") || "the first contact",
          household: ra.household,
        },
        {
          contactLabel:
            displayLabel(cluster[1]!.label ?? "") || "the other contact",
          household: rb.household,
        },
      );
      if (plan.kind === "same") {
        setLinkHhNote({
          tone: "info",
          text: `Already linked — both are members of “${displayLabel(
            plan.householdLabel,
          )}”. Nothing to merge; apply “keep separate” below and this issue is done.`,
        });
        setChosen("dismiss");
        if (!rationale.trim()) {
          setRationale(
            "A couple in one household — kept separate (not a duplicate).",
          );
        }
      } else if (plan.kind === "unlinked") {
        setLinkHhNote({
          tone: "warn",
          text: `${plan.names.join(" and ")} ${
            plan.names.length === 1 ? "isn't" : "aren't"
          } in any household yet — click the name in the research pane to open the record and set its household first, then link here.`,
        });
      } else {
        onRequestHouseholdFieldMerge(plan.refs, {
          defaultSurvivorId: plan.defaultSurvivorId,
          initialReason:
            "Linked a couple into one household via Cleanup — two people sharing contact info, not a duplicate.",
        });
      }
    } catch (e) {
      setLinkHhNote({
        tone: "warn",
        text:
          e instanceof ApiError && e.status === 404
            ? "One of these records no longer exists — it was likely already merged or archived. This issue is stale; dismiss it, or it clears on the next scan."
            : "Couldn't look up the two households. Nothing was changed — try again.",
      });
    } finally {
      setLinkHhBusy(false);
    }
  }

  async function undo() {
    if (!done || !done.undo.available) return;
    setBusy(true);
    setErr(null);
    try {
      await api.undoCleanupDecision({
        issue_id: issue!.id,
        kind: done.undo.kind,
        refs: done.undo.refs,
      });
      setDone(null);
      onResolved();
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? e.message
          : "Undo failed — use the record’s History tab.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Rail>
        <div className="cu-dr-s20">
          <b>Done — {done.verb}.</b>{" "}
          {consequence(done.verb as Verb, issue, null, expirationDate, clientSinceDate, effectiveDate, entityName, clientSinceIsFloor, programType, programTypeNote, maritalStatus, undefined, contactEmail, contactPhone, salePrice, primaryPick, primaryCandidates)}
        </div>
        {done.undo.available && (
          <Button
            variant="ghost"
            onClick={undo}
            disabled={busy}
            style={{ marginTop: 12 }}
          >
            Undo (within 60s)
          </Button>
        )}
        {err && (
          <div className="cu-dr-s3">
            {err}
          </div>
        )}
      </Rail>
    );
  }

  return (
    <Rail>
      {issue.recommendation ? (
        <div className="cu-rec">
          <div className="cu-rec__lbl">
            <Zap size={11} strokeWidth={2.25} aria-hidden />
            Recommended
          </div>
          <div className="cu-rec__title">
            {issue.recommendation.label}
          </div>
          <div className="cu-rec__reason">
            {issue.recommendation.reason}
          </div>
        </div>
      ) : (
        <div className="cu-dr-s21">
          No automatic recommendation — research, then choose an action.
        </div>
      )}

      {/* P1-5 (cleanup UX audit, 2026-07-13) — side-by-side comparison
          for insurance policy-conflict duplicates. Before this, Joy's
          rail listed only the household name; she had to open the
          record, find Policies & Services, and hunt for the two rows
          herself (~8 clicks). Each row is one of the conflicting
          policies (carrier · type · status · premium · expiration ·
          source, from the detector's rowDetail()); "Keep this one"
          picks the survivor for the merge — the Apply button below
          executes it. */}
      {isDuplicatePolicyIssue(issue) &&
        duplicatePolicyRows(issue).length >= 2 && (
          <div className="cu-dr-s22">
            <div className="cu-dr-s23">
              Which row is the real policy?
            </div>
            <div className="cu-dr-s24">
              {duplicatePolicyRows(issue).map((row) => {
                const defaultSurvivorId =
                  typeof issue.recommendation?.params?.["survivor_id"] ===
                  "string"
                    ? (issue.recommendation.params["survivor_id"] as string)
                    : null;
                const effectiveSurvivorId =
                  dupPolicySurvivorId ?? defaultSurvivorId;
                const isKept = effectiveSurvivorId === row.id;
                return (
                  <div
                    key={row.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      padding: "7px 10px",
                      borderRadius: 6,
                      border: isKept
                        ? "1px solid var(--blue)"
                        : "1px solid var(--mid-gray)",
                      background: isKept ? "var(--blue-tint)" : "var(--bg-0)",
                    }}
                  >
                    <span className="cu-dr-s25">
                      {row.detail}
                    </span>
                    <Button
                      variant={isKept ? "primary" : "sw-default"}
                      size="sm"
                      disabled={busy}
                      onClick={() => setDupPolicySurvivorId(row.id)}
                    >
                      {isKept ? "Keeping this one" : "Keep this one"}
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="cu-dr-s26">
              {duplicatePolicyConsequence(
                duplicatePolicyRows(issue),
                dupPolicySurvivorId ??
                  (typeof issue.recommendation?.params?.["survivor_id"] ===
                  "string"
                    ? (issue.recommendation.params["survivor_id"] as string)
                    : null),
              )}
            </div>
          </div>
        )}

      {/* "Fixed — mark resolved" (P1-4, Mylah onboarding 2026-07-17).
          A manual / needs-attention item won't clear on its own — and
          "Not an issue" is the wrong close (it WAS an issue; you fixed
          it, and it skews the feedback stats). This gives a manual item a
          true "I handled it" close, distinct from dismissal: it resolves
          the issue (acknowledge verb → resolved), reversible for 60s.
          Only for no-recommendation items (a recommendation IS the resolve
          path) and non-confirm tiers (confirm needs the reason picker
          below, so those keep the choose→Apply flow). Suppressed on the
          twin picker: that block already offers both real answers, and a
          third generic "mark resolved" next to them would resolve the
          issue while leaving the stub exactly where it was. */}
      {app.noRecommendation && issue.tier !== "confirm" && !done && !twinPick && (
        <div className="cu-dr-s27">
          <div className="cu-dr-s28">
            Already handled this? If you&rsquo;ve added the policy, verified at
            the carrier, or reviewed it and nothing needs to change, mark it
            resolved — it won&rsquo;t leave your list on its own.
          </div>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => act("acknowledge", false)}
            style={{ width: "100%" }}
          >
            {busy ? "Applying…" : "Fixed — mark resolved"}
          </Button>
          <div className="cu-dr-s29">
            Clears it from your list; nothing on the record changes.
            Reversible for 60 seconds (it reopens).
          </div>
        </div>
      )}

      {/* Donna 2026-07-14 — current assignment indicator. When this issue
          has been handed to a teammate, say so plainly (near the top, not
          buried), so it's obvious it's in someone's queue. */}
      {issue.assignedOwnerUserId && (
        <div className="cu-dr-s30">
          <UserCheck size={14} strokeWidth={1.9} aria-hidden />
          Assigned to{" "}
          <b>
            {currentAssignee?.name ?? currentAssignee?.email ?? "a teammate"}
          </b>
        </div>
      )}

      {/* before→after, UX rule #3 */}
      <div className="cu-dr-s31">
        {consequence(
          activeVerb,
          issue,
          advisorOptions.find((u) => u.id === advisorId)?.name ?? null,
          expirationDate,
          clientSinceDate,
          effectiveDate,
          entityName,
          clientSinceIsFloor,
          programType,
          programTypeNote,
          maritalStatus,
          feeAmount,
          contactEmail,
          contactPhone,
          salePrice,
          primaryPick,
          primaryCandidates,
        )}
      </div>

      {/* Joy 2026-07-01 — the expiration issues need the DATE before
          verify can do what its label says ("confirm at the carrier and
          set it"). */}
      {expirationIssue && activeVerb === "verify" && (
        <div className="cu-dr-s32">
          <label
            htmlFor="cu-expiration-date" className="cu-dr-s33">
            Expiration date (from the carrier’s site)
            {expirationDateRequired(issue) ? "" : " — optional"}
          </label>
          <DateField
            id="cu-expiration-date"
            value={expirationDate}
            onChange={(v) => {
              setExpirationDate(v);
              if (err) setErr(null);
            }}
            style={{
              width: "100%",
              border: "1px solid var(--mid-gray)",
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 13,
              color: "var(--navy)",
              background: "var(--bg-0)",
              boxSizing: "border-box",
            }}
          />
          {!expirationDateRequired(issue) && (
            <div className="cu-dr-s34">
              If it renewed at the carrier, enter the new expiration so
              the OS copy refreshes. Leave blank if the date on file is
              already right.
            </div>
          )}
        </div>
      )}

      {/* Nate 2026-07-02 — the client-since issue needs the DATE before
          verify can do what its label says ("set the client-since
          date"). */}
      {clientSinceIssue && activeVerb === "verify" && (
        <div className="cu-dr-s32">
          <label
            htmlFor="cu-client-since-date" className="cu-dr-s33">
            Client-since date (when the relationship began)
          </label>
          <DateField
            id="cu-client-since-date"
            value={clientSinceDate}
            onChange={(v) => {
              setClientSinceDate(v);
              if (err) setErr(null);
            }}
            style={{
              width: "100%",
              border: "1px solid var(--mid-gray)",
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 13,
              color: "var(--navy)",
              background: "var(--bg-0)",
              boxSizing: "border-box",
            }}
          />
          <div className="cu-dr-s34">
            From the original agreement / first policy. Only sure of the
            year? Enter the earliest date you're confident of (e.g.
            January 1 of that year) and tick the box below.
          </div>
          {/* Grant 2026-07-12 — exact vs floor. Unticked = exact date;
              ticked = "client since at least <date>" (migration 0200). */}
          <label className="cu-dr-s35">
            <input
              type="checkbox"
              checked={clientSinceIsFloor}
              onChange={(e) => setClientSinceIsFloor(e.target.checked)} className="cu-dr-s36"
            />
            <span>
              This is the <b>earliest date I know of</b>, not the exact
              start — record it as “client since at least …”.
            </span>
          </label>
          {/* Grant 2026-07-12 — the honest terminal answer when nobody
              knows the date. One extra click, never a dead end; same
              escape shape as the PM entity "not known" button. */}
          <Button
            variant="ghost"
            onClick={() => act("keep-current", false)}
            disabled={busy}
            style={{ width: "100%", marginTop: 10 }}
          >
            {CLIENT_SINCE_UNKNOWN_LABEL}
          </Button>
          <div className="cu-dr-s34">
            {clientSinceUnknownConsequence()}
          </div>
        </div>
      )}

      {/* Baker 2026-07-31 — the realty zero-price issue needs the PRICE
          before verify can do what the card asks ("What is the correct
          price for …?"). Until this, verify resolved the card with
          nothing written: the price stayed $0 and the queue went quiet.
          The "this wasn't a sale" answer is the generic Not-an-issue
          dismissal below — it's durable, since a resolved issue is never
          reopened by re-detection. */}
      {realtyPriceIssue && activeVerb === "verify" && (
        <div className="cu-dr-s32">
          <label
            htmlFor="cu-sale-price" className="cu-dr-s33">
            Sale price ($)
          </label>
          <input
            id="cu-sale-price"
            type="text"
            inputMode="decimal"
            placeholder="e.g. 425,000"
            value={salePrice}
            onChange={(e) => {
              setSalePrice(e.target.value);
              if (err) setErr(null);
            }} className="cu-dr-s37"
          />
          <div className="cu-dr-s34">
            What the property sold for — BrokerMint's Price column, copied
            across. Dollars, e.g. 425,000. If this wasn't a sale (a
            referral fee, say), use “Not an issue” below and note what it
            actually was.
          </div>
        </div>
      )}

      {/* Baker 2026-07-13 — the pre-2018 PM revenue-gap issue needs the
          AMOUNT before verify can do what its label says ("enter the
          amount earned & mark verified"). Escape when it genuinely can't
          be determined. */}
      {pmFeeIssue && activeVerb === "verify" && (
        <div className="cu-dr-s32">
          <label
            htmlFor="cu-fee-amount" className="cu-dr-s33">
            Amount earned — Stewardship’s fee ($)
          </label>
          <input
            id="cu-fee-amount"
            type="text"
            inputMode="decimal"
            placeholder="e.g. 1,500"
            value={feeAmount}
            onChange={(e) => {
              setFeeAmount(e.target.value);
              if (err) setErr(null);
            }} className="cu-dr-s37"
          />
          <div className="cu-dr-s34">
            The total Stewardship was paid on this Private Money loan
            (from Baker’s historical records). Dollars — e.g. 1,500 or
            1500.50.
          </div>
          {/* The honest terminal answer when the fee genuinely can't be
              reconstructed. Same escape shape as the client-since
              "we don't know" button. */}
          <Button
            variant="ghost"
            onClick={() => act("keep-current", false)}
            disabled={busy}
            style={{ width: "100%", marginTop: 10 }}
          >
            {PM_FEE_UNKNOWN_LABEL}
          </Button>
          <div className="cu-dr-s34">
            {pmFeeUnknownConsequence()}
          </div>
        </div>
      )}

      {/* Baker 2026-07-13 — hh-quarantined: the three answers to "Is
          this a real, distinct household?" are all direct actions here
          (the old recommendation dead-ended at "open the full review
          screen"). Keep = clear the flag; merge = open the existing
          search-and-merge picker; archive = the generic archive verb
          (refused with a plain-language note if anything active is
          attached). */}
      {quarantineIssue && !hasChoice && (
        <div className="cu-dr-s32">
          <Button
            variant="primary"
            onClick={() => act("keep-current", false)}
            disabled={busy}
            style={{ width: "100%" }}
          >
            {QUARANTINE_KEEP_LABEL}
          </Button>
          <div className="cu-dr-s38">
            {quarantineKeepConsequence()}
          </div>
          {searchMergeSource && onRequestHouseholdSearchMerge && (
            <>
              <Button
                variant="sw-default"
                onClick={() =>
                  onRequestHouseholdSearchMerge(searchMergeSource)
                }
                disabled={busy}
                style={{ width: "100%" }}
              >
                {QUARANTINE_MERGE_LABEL}
              </Button>
              <div className="cu-dr-s38">
                Search for the family this really belongs to, review every
                field, then merge. Nothing changes until you confirm;
                reversible after.
              </div>
            </>
          )}
          <Button
            variant="ghost"
            onClick={() => act("archive", false)}
            disabled={busy}
            style={{ width: "100%" }}
          >
            {QUARANTINE_ARCHIVE_LABEL}
          </Button>
          <div className="cu-dr-s34">
            {quarantineArchiveConsequence()}
          </div>
        </div>
      )}

      {/* Baker 2026-07-13 — vendor-lookalike: the answers to "Is this a
          business, not a person?" are all direct actions here (the old
          recommendation dead-ended at "needs the full review screen").
          Grant 2026-08-10 added the third: a business we SERVE is neither
          a vendor nor a person, and it used to have no true answer. */}
      {vendorLookalikeIssue && !hasChoice && (
        <div className="cu-dr-s32">
          <Button
            variant="primary"
            onClick={() => act("retag-as-vendor", false)}
            disabled={busy}
            style={{ width: "100%" }}
          >
            {VENDOR_LOOKALIKE_RETAG_LABEL}
          </Button>
          <div className="cu-dr-s38">
            Reclassifies this record as a vendor (title company, lender,
            partner, …) — a business that serves Stewardship. It stops
            appearing in person-facing surfaces. Reversible via the
            record’s History tab.
          </div>
          <Button
            variant="sw-default"
            onClick={() =>
              act(
                "keep-current",
                false,
                undefined,
                "Business entity we serve — a client, not a vendor",
              )
            }
            disabled={busy}
            style={{ width: "100%" }}
          >
            {VENDOR_LOOKALIKE_CLIENT_LABEL}
          </Button>
          <div className="cu-dr-s38">
            {vendorLookalikeClientEntityConsequence()}
          </div>
          <Button
            variant="ghost"
            onClick={() => act("dismiss", false)}
            disabled={busy}
            style={{ width: "100%" }}
          >
            {VENDOR_LOOKALIKE_PERSON_LABEL}
          </Button>
          <div className="cu-dr-s34">
            {vendorLookalikePersonConsequence()}
          </div>
        </div>
      )}

      {/* Donna 2026-07-30 — combined-contact: the two answers to
          "split or remove it?" are both direct actions here (the old
          recommendation pointed at the contact page, which has no split
          affordance — a dead end). When the detector says both people
          already exist separately, archive leads; otherwise split
          leads. */}
      {combinedContactIssue && !hasChoice && issue && (
        <div className="cu-dr-s32">
          {(() => {
            const prefersArchive = combinedContactPrefersArchive(issue);
            const splitButton = (
              <>
                <Button
                  variant={prefersArchive ? "sw-default" : "primary"}
                  onClick={() => act("verify", prefersArchive)}
                  disabled={busy}
                  style={{ width: "100%" }}
                >
                  {combinedContactSplitLabel(issue)}
                </Button>
                <div className="cu-dr-s38">
                  {combinedContactSplitConsequence()}
                </div>
              </>
            );
            const archiveButton = (
              <>
                <Button
                  variant={prefersArchive ? "primary" : "ghost"}
                  onClick={() => act("archive", !prefersArchive)}
                  disabled={busy}
                  style={{ width: "100%" }}
                >
                  {COMBINED_CONTACT_ARCHIVE_LABEL}
                </Button>
                <div className="cu-dr-s38">
                  {combinedContactArchiveConsequence(prefersArchive)}
                </div>
              </>
            );
            return prefersArchive ? (
              <>
                {archiveButton}
                {splitButton}
              </>
            ) : (
              <>
                {splitButton}
                {archiveButton}
              </>
            );
          })()}
        </div>
      )}

      {/* Donna + Baker 2026-07-30 — hh-empty archived-member variant:
          the two answers to "who should this household belong to?" are
          both direct actions here (the old recommendation asked for an
          un-archive the flow never offered — "I have no way of doing
          this"). */}
      {archivedMemberIssue && !hasChoice && issue && (
        <div className="cu-dr-s32">
          {(() => {
            // Grant 2026-07-30 — when archiving the member left a shell
            // with nothing active attached, archiving the household is
            // the RECOMMENDED answer (it leads, and isn't an override).
            // With active work attached the archive is blocked, so
            // un-archive leads instead.
            const prefersArchive = archivedMemberPrefersArchive(issue);
            const unarchiveButton = (
              <>
                <Button
                  variant={prefersArchive ? "sw-default" : "primary"}
                  onClick={() => act("verify", prefersArchive)}
                  disabled={busy}
                  style={{ width: "100%" }}
                >
                  {ARCHIVED_MEMBER_UNARCHIVE_LABEL}
                </Button>
                <div className="cu-dr-s38">
                  {archivedMemberUnarchiveConsequence()}
                </div>
              </>
            );
            const archiveButton = (
              <>
                <Button
                  variant={prefersArchive ? "primary" : "ghost"}
                  onClick={() => act("archive", !prefersArchive)}
                  disabled={busy}
                  style={{ width: "100%" }}
                >
                  {ARCHIVED_MEMBER_CLOSED_LABEL}
                </Button>
                <div className="cu-dr-s38">
                  {archivedMemberClosedConsequence(prefersArchive)}
                </div>
              </>
            );
            return prefersArchive ? (
              <>
                {archiveButton}
                {unarchiveButton}
              </>
            ) : (
              <>
                {unarchiveButton}
                {archiveButton}
              </>
            );
          })()}
        </div>
      )}

      {/* Grant 2026-07-13 — the marital issue needs the STATUS before
          verify can do what its label says ("set the marital status").
          Routes to the household's advisor; escape when nobody knows. */}
      {maritalIssue && activeVerb === "verify" && (
        <div className="cu-dr-s32">
          <label
            htmlFor="cu-marital-status" className="cu-dr-s33">
            Marital status
          </label>
          <select
            id="cu-marital-status"
            value={maritalStatus}
            onChange={(e) => {
              setMaritalStatus(e.target.value);
              if (err) setErr(null);
            }} className="cu-dr-s37">
            <option value="">Pick one…</option>
            {MARITAL_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          {/* Grant 2026-07-13 — the honest terminal answer when nobody
              knows. One extra click, never a dead end; same escape shape
              as the client-since "we don't know" button. */}
          <Button
            variant="ghost"
            onClick={() => act("keep-current", false)}
            disabled={busy}
            style={{ width: "100%", marginTop: 10 }}
          >
            {MARITAL_UNKNOWN_LABEL}
          </Button>
          <div className="cu-dr-s34">
            {maritalUnknownConsequence()}
          </div>
        </div>
      )}

      {/* Baker 2026-07-31 — hh-no-primary. The member picker IS the
          action: choosing one sets the pointer AND demotes every other
          claimant in the same step. Replaces "open the household record
          and pick a member", which dead-ended on the multi-primary
          variant (the Edit form already named the right person, so no
          save the steward could make changed anything). The honest
          "we don't know" escape stays below it. */}
      {primaryIssue && (
        <div className="cu-dr-s32">
          {primaryLoading && (
            <div className="cu-dr-s39">
              Loading this household's members…
            </div>
          )}
          {primaryLoadErr && (
            <div className="cu-dr-s40">
              {primaryLoadErr}
            </div>
          )}
          {!primaryLoading && !primaryLoadErr && primaryCandidates.length > 0 && (
            <>
              <div className="cu-dr-s40">
                {primaryPickPrompt(primaryCandidates)}
              </div>
              <fieldset className="cu-dr-s41">
                <legend className="cu-dr-s42">
                  Primary contact
                </legend>
                {primaryCandidates.map((cand) => (
                  <label
                    key={cand.id}
                    className="cu-radio-pick-row cu-dr-s43">
                    <input
                      type="radio"
                      name="cu-primary-pick"
                      value={cand.id}
                      checked={primaryPick === cand.id}
                      onChange={() => setPrimaryPick(cand.id)}
                      disabled={busy}
                    />
                    <span>{cand.name}</span>
                    {/* Name BOTH kinds of "primary" on the row. The
                        household's pointer and a member's own role can
                        disagree — that disagreement is the whole issue,
                        and hiding it is what made this unanswerable. */}
                    {cand.isPointer && (
                      <span className="cu-dr-s44">
                        currently listed as primary
                      </span>
                    )}
                    {cand.claimsPrimary && !cand.isPointer && (
                      <span className="cu-dr-s44">
                        also marked primary
                      </span>
                    )}
                  </label>
                ))}
              </fieldset>
            </>
          )}
          {!primaryLoading && !primaryLoadErr && primaryCandidates.length === 0 && (
            <div className="cu-dr-s40">
              This household has no active members to choose from. Record
              that nobody knows the primary below, or open the household
              record to add a member.
            </div>
          )}
          <Button
            variant="ghost"
            onClick={() => act("keep-current", false)}
            disabled={busy}
            style={{ width: "100%", marginTop: 10 }}
          >
            {PRIMARY_UNKNOWN_LABEL}
          </Button>
          <div className="cu-dr-s34">
            {primaryUnknownConsequence()}
          </div>
        </div>
      )}

      {/* Grant 2026-07-12 — the program-type issue needs the PROGRAM
          before verify can do what its label says ("pick the loan
          program & resolve"). Full option set per Grant's ruling:
          "sometimes we get HELOC or Reverse or Non QM or Other". */}
      {programTypeIssue && activeVerb === "verify" && (
        <div className="cu-dr-s32">
          <label
            htmlFor="cu-program-type" className="cu-dr-s33">
            Loan program
          </label>
          <select
            id="cu-program-type"
            value={programType}
            onChange={(e) => {
              setProgramType(e.target.value);
              if (err) setErr(null);
            }} className="cu-dr-s37">
            <option value="">Pick the loan program…</option>
            {TRADITIONAL_LOAN_PROGRAM_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <div className="cu-dr-s34">
            Use the lender, purpose, and amount in the evidence. “Other”
            is a real answer — it sticks and won't be re-asked.
          </div>
          {programType === "other" && (
            <input
              id="cu-program-type-note"
              type="text"
              value={programTypeNote}
              onChange={(e) => setProgramTypeNote(e.target.value)}
              placeholder="Optional — what is it? (e.g. wrap loan)"
              maxLength={300} className="cu-dr-s45"
            />
          )}
        </div>
      )}

      {/* Grant 2026-07-10 QA — the missing-effective-date issue needs
          the DATE before verify can do what its label says ("enter
          the start date and apply"). */}
      {effectiveDateIssue && activeVerb === "verify" && (
        <div className="cu-dr-s32">
          <label
            htmlFor="cu-effective-date" className="cu-dr-s33">
            Start (effective) date
          </label>
          <DateField
            id="cu-effective-date"
            value={effectiveDate}
            onChange={(v) => {
              setEffectiveDate(v);
              if (err) setErr(null);
            }}
            style={{
              width: "100%",
              border: "1px solid var(--mid-gray)",
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 13,
              color: "var(--navy)",
              background: "var(--bg-0)",
              boxSizing: "border-box",
            }}
          />
          <div className="cu-dr-s34">
            From the policy, loan document, or signed agreement.
          </div>
          {/* Grant-approved 2026-07-14 — the honest terminal answer when
              the start date genuinely can't be recovered. One extra click,
              never a dead end; same escape shape as the marital / client-
              since "we don't know" buttons. */}
          <Button
            variant="ghost"
            onClick={() => act("keep-current", false)}
            disabled={busy}
            style={{ width: "100%", marginTop: 10 }}
          >
            {EFFECTIVE_DATE_UNKNOWN_LABEL}
          </Button>
          <div className="cu-dr-s34">
            {effectiveDateUnknownConsequence()}
          </div>
        </div>
      )}

      {/* P1-6 (cleanup UX audit, 2026-07-13) — contact-no-contact-method.
          Was a dead end ("open the record to add email or phone"); now an
          inline quick-edit, same pattern as the client-since date input
          above. Typing either field arms the primary Apply button; the
          "keep record anyway" escape stays live for genuinely neither. */}
      {noContactMethodIssue && activeVerb === "verify" && (
        <div className="cu-dr-s32">
          <label
            htmlFor="cu-contact-email" className="cu-dr-s33">
            Email
          </label>
          <input
            id="cu-contact-email"
            type="email"
            value={contactEmail}
            onChange={(e) => {
              setContactEmail(e.target.value);
              if (err) setErr(null);
            }}
            placeholder="name@example.com" className="cu-dr-s46"
          />
          <label
            htmlFor="cu-contact-phone" className="cu-dr-s33">
            Phone
          </label>
          <input
            id="cu-contact-phone"
            type="tel"
            value={contactPhone}
            onChange={(e) => {
              setContactPhone(e.target.value);
              if (err) setErr(null);
            }}
            placeholder="(555) 555-5555" className="cu-dr-s37"
          />
          <div className="cu-dr-s34">
            From the source record. Adding either is the whole action —
            you don't need both.
          </div>
          <Button
            variant="ghost"
            onClick={() => act("keep-current", false)}
            disabled={busy}
            style={{ width: "100%", marginTop: 10 }}
          >
            {NO_CONTACT_METHOD_KEEP_LABEL}
          </Button>
          <div className="cu-dr-s34">
            {noContactMethodKeepConsequence()}
          </div>
        </div>
      )}

      {/* Grant-approved 2026-07-14 — mortgage-missing-funded-date. The
          recommended action opens the loan to set the funded date; this is
          the honest terminal answer when it genuinely can't be recovered. */}
      {fundedDateUnknownIssue && (
        <div className="cu-dr-s32">
          <div className="cu-dr-s40">
            Open the loan to set the funded date from the closing/funding
            documents if you have them. If the funded date genuinely can't be
            recovered, record that below — it won't be asked again.
          </div>
          <Button
            variant="ghost"
            onClick={() => act("keep-current", false)}
            disabled={busy}
            style={{ width: "100%" }}
          >
            {FUNDED_DATE_UNKNOWN_LABEL}
          </Button>
          <div className="cu-dr-s34">
            {fundedDateUnknownConsequence()}
          </div>
        </div>
      )}

      {/* Nate 2026-07-14 — realty-pending: if it closed, mark it closed on
          the record (the item clears itself once a close date is set). The
          escape below is the honest answer for a listing/search that isn't
          under contract yet — no close date to give — one click, no dead
          end. */}
      {realtyStillActiveIssue && (
        <div className="cu-dr-s32">
          <div className="cu-dr-s40">
            If this has closed, open the record and mark it closed — set the
            close date on the Status card (this item clears itself once a
            close date is set). If it's still active and not under contract
            yet, there's no close date to give — record that below and it
            won't be asked again.
          </div>
          <Button
            variant="ghost"
            onClick={() => act("keep-current", false)}
            disabled={busy}
            style={{ width: "100%" }}
          >
            {REALTY_STILL_ACTIVE_LABEL}
          </Button>
          <div className="cu-dr-s34">
            {realtyStillActiveConsequence()}
          </div>
        </div>
      )}

      {/* Grant 2026-07-11 — pm-entity-name-needed: typing the entity
          name IS the action (creates the business record + moves the
          loans). The escape button below is the honest "not known"
          terminal answer — one extra click, never a dead end. */}
      {pmEntityIssue && activeVerb === "verify" && (
        <div className="cu-dr-s32">
          <label
            htmlFor="cu-entity-name" className="cu-dr-s33">
            Business entity name (LLC / trust / corporation)
          </label>
          <input
            id="cu-entity-name"
            type="text"
            value={entityName}
            onChange={(e) => {
              setEntityName(e.target.value);
              if (err) setErr(null);
            }}
            placeholder="e.g. Sunrise Holdings LLC" className="cu-dr-s37"
          />
          <div className="cu-dr-s34">
            Typing it creates the entity, moves the Private Money loans
            onto it, links the person as principal, and sets Mike Zins as
            its advisor — all in one step.
          </div>
          <Button
            variant="ghost"
            onClick={() => act("keep-current", false, "individual_business_purpose")}
            disabled={busy}
            style={{ width: "100%", marginTop: 8 }}
          >
            {PM_ENTITY_INDIVIDUAL_LABEL}
          </Button>
          <div className="cu-dr-s34">
            {pmEntityIndividualConsequence()}
          </div>
          <Button
            variant="ghost"
            onClick={() => act("keep-current", false, "not_known")}
            disabled={busy}
            style={{ width: "100%", marginTop: 8 }}
          >
            {PM_ENTITY_KEEP_PERSONAL_LABEL}
          </Button>
          <div className="cu-dr-s34">
            {pmEntityKeepPersonalConsequence()}
          </div>
        </div>
      )}

      {/* Audit 6.2 — advisor-gap issues: pick WHO before assigning;
          the Apply button below then names them. */}
      {advisorGapIssue && activeVerb === "match" && (
        <div className="cu-dr-s32">
          <label
            htmlFor="cu-advisor-pick" className="cu-dr-s33">
            Who owns this record?
          </label>
          <select
            id="cu-advisor-pick"
            value={advisorId}
            onChange={(e) => {
              setAdvisorId(e.target.value);
              if (err) setErr(null);
            }} className="cu-dr-s37">
            <option value="">Pick the advisor…</option>
            {advisorOptions.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name ?? u.email}
                {u.role_slug ? ` · ${u.role_slug}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Per-field cluster merge — the user chooses what survives,
          field by field (reuses the tested orchestrator/wizard). The
          primary path for a contact cluster; quick-merge stays below
          for the simple 2-record case. */}
      {cluster && (
        <div className="cu-dr-s32">
          <Button
            variant="primary"
            onClick={() =>
              onRequestFieldMerge(
                cluster,
                duplicateMatchType(issue.detectorSlug),
                issue.decisionQuestion,
              )
            }
            disabled={busy}
            style={{ width: "100%" }}
          >
            Review every field, then merge…
          </Button>
          <div className="cu-dr-s47">
            {cluster.length === 2 ? (
              <>
                Opens a side-by-side of{" "}
                <b>{displayLabel(cluster[0]!.label)}</b> and{" "}
                <b>{displayLabel(cluster[1]!.label)}</b>.
              </>
            ) : (
              <>
                Opens the full review of all{" "}
                <b>{cluster.length} records</b> — you pick the survivor,
                then merge each one in with per-field control.
              </>
            )}{" "}
            Click each field to pick the value that survives (or keep
            both). Nothing changes until you confirm; reversible after.
          </div>
        </div>
      )}

      {/* Cambria 2026-07-31 — "i figured out which household is the
          right one but im not seeing a spot where i can fix it." The
          answer to "Which real household is this?" now has a place to
          go: one row per candidate family, each opening the SAME
          per-field merge review the other household paths use, with
          that family pre-selected as the survivor and the stub as the
          record being absorbed. Never a blind merge — every field is
          reviewed and the merge is reversible.

          Shaped like the duplicate-policy "Which row is the real
          policy?" block above (P1-5), for the same reason: when the
          question names N options, the answer belongs next to them. */}
      {/* Baker 2026-07-31 — the placeholder this card is about has
          already been merged into a real household, so there is nothing
          left to merge and every candidate row would fail the way his
          did ("this household is already merged into another household,
          so it no longer exists"). Both cleanup guards now close such a
          card at merge time, so reaching this state means an already-open
          tab or a direct link to the issue — which is exactly when a
          plain sentence and a real close beat a button that 404s.

          This is also the only place a "mark it handled" answer is
          offered on a twin card, deliberately: it renders ONLY when the
          placeholder is provably gone, so it can't become a generic
          escape hatch that resolves the issue while leaving the
          placeholder exactly where it was. */}
      {twinPick?.stubGone && (
        <div className="cu-dr-s27">
          <div className="cu-dr-s48">
            Already taken care of
          </div>
          <div className="cu-dr-s28">
            This placeholder has already been merged into a real
            household, so its mortgage records are no longer sitting on
            it. There is nothing left to pick — the question this item
            asked has been answered.
          </div>
          <Button
            variant="primary"
            disabled={busy || done != null}
            style={{ width: "100%" }}
            onClick={() =>
              act(
                "acknowledge",
                false,
                undefined,
                "The placeholder had already been merged into its real household, so there was nothing left to place.",
                "data_quality_cleanup",
              )
            }
          >
            {busy ? "Closing…" : "Close this item"}
          </Button>
          <div className="cu-dr-s47">
            Nothing changes on any record. Reversible for 60 seconds, and
            anyone can reopen it later.
          </div>
        </div>
      )}

      {twinPick && !twinPick.stubGone && (
        <div className="cu-dr-s49">
          <div className="cu-dr-s50">
            Which of these is the real family?
          </div>
          <div className="cu-dr-s51">
            Pick one and its mortgage records move off the placeholder
            and onto that household. You&rsquo;ll see every field
            side-by-side before anything happens, and it&rsquo;s
            reversible after.
          </div>
          <div className="cu-dr-s24">
            {twinPick.candidates.map((candidate) => (
              <div
                key={candidate.id} className="cu-dr-s52">
                <span className="cu-dr-s53">
                  <span className="cu-dr-s54">
                    {displayLabel(candidate.label)}
                  </span>
                  {/* "Which Jones?" — without this, two candidate rows
                      read as the identical name and the pick is a coin
                      flip (these issues exist BECAUSE the names match).
                      Server-attached at list time. When a household has
                      no named members to show, fall back to the tail of
                      its id: ugly, but it matches the id in the "Open
                      page" links above, and two indistinguishable rows
                      are worse than one unlovely one. */}
                  <span className="cu-dr-s55">
                    {candidate.members_label ||
                      `record ending ${candidate.id.slice(-6)}`}
                  </span>
                  {/* This family has been merged away or archived since
                      the item was flagged, so merging into it is not a
                      thing that can happen. Say so here rather than
                      letting the button fail against a record that no
                      longer exists. The row stays visible (not filtered)
                      so the list still reflects what was asked. */}
                  {candidate.record_gone === true && (
                    <span className="cu-dr-s56">
                      No longer a household on its own — it has since been
                      merged or archived. Open its page from the records
                      above to see where it went.
                    </span>
                  )}
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy || candidate.record_gone === true}
                  onClick={() =>
                    onRequestHouseholdFieldMerge(
                      // [absorbed, survivor] — the overlay falls back to
                      // refs[1] as survivor, which lands on the family
                      // either way. defaultSurvivorId states it anyway
                      // rather than leaning on position, so the stub can
                      // never end up absorbing the real household.
                      [twinPick.stub, candidate],
                      {
                        defaultSurvivorId: candidate.id,
                        initialReason:
                          "Mortgage-import placeholder reattached to the client's real household during cleanup.",
                      },
                    )
                  }
                >
                  This is the family…
                </Button>
              </div>
            ))}
          </div>
          {/* The other real answer. Two people can genuinely share a
              name — without this the only honest close was buried under
              "or do something else" as the generic duplicate wording,
              which is not what someone looking at a placeholder is
              looking for. Carries its own reason code because the
              confirm tier requires one server-side and this path
              suppresses the picker. */}
          <div className="cu-dr-s57">
            <Button
              variant="sw-default"
              disabled={busy}
              style={{ width: "100%" }}
              onClick={() =>
                act(
                  "dismiss",
                  false,
                  undefined,
                  "None of the named households is this person — the placeholder stays its own household.",
                  "data_quality_cleanup",
                )
              }
            >
              None of these — it&rsquo;s a different person
            </Button>
            <div className="cu-dr-s47">
              Nothing merges; this household stays as it is and stops
              being asked about. Reversible for 60 seconds, and anyone
              can reopen it later.
            </div>
          </div>
        </div>
      )}

      {/* Baker 2026-05-22 — parallel button for HOUSEHOLD-level merge
          issues (the mortgage import placeholder-household merge). Opens
          HouseholdClusterMergeOverlay directly on the per-field review,
          with the recommended survivor pre-selected + a one-click swap
          (Grant 2026-07-10 — the old two-step "pick survivor first, THEN
          see the fields" made you choose blind). Still never a blind
          one-click merge: every field is reviewed before submit. */}
      {/* Grant 2026-09-15 — the import placeholder beside the family its
          loan points at. Three answers, because the steward's conclusion
          is one of three things and until now NONE of them had a button:
          this card carries two household refs and no recommendation, so
          the search-merge path (one ref) and the generic cluster path
          (needs a merge verb) both declined it and the person was left
          with snooze and assign.

          Two of the three run the SAME household merge and differ only
          in the reason recorded, which is deliberate and not a trick:
          putting the placeholder into the family is the right move
          either way, and which call was made is what the next person
          needs to know. When it is one person, the review now also offers
          (ticked) to combine the two person records in the same merge
          (Grant 2026-09-30). The "within-household stub lane" this used to
          promise never matched two different surnames, so every "same
          person" answer left the old card behind. */}
      {stubWithCandidate && householdCluster && !done && (
        <div className="cu-dr-s49">
          <div className="cu-dr-s50">
            What did you find?
          </div>
          <div className="cu-dr-s51">
            The import made{" "}
            <b>{displayLabel(householdCluster[0]!.label)}</b> its own
            household. Its loan points at{" "}
            <b>{displayLabel(householdCluster[1]!.label)}</b>. Every
            answer below shows you each field side-by-side first, and is
            reversible after.
          </div>
          <div className="cu-dr-s24">
            <Button
              variant="primary"
              disabled={busy}
              style={{ width: "100%" }}
              onClick={() =>
                onRequestHouseholdFieldMerge(householdCluster, {
                  defaultSurvivorId: householdCluster[1]!.id,
                  initialReason:
                    "Mortgage-import placeholder is the same person under a different surname — merged into their household.",
                  samePersonConfirmed: true,
                })
              }
            >
              Same person, different surname &mdash; review &amp; merge&hellip;
            </Button>
            <div className="cu-dr-s58">
              A married name, a maiden name, a name the report spelled its
              own way. The loan moves onto the family, the placeholder
              household goes away, and the two person records are combined
              into one (it starts ticked on the next screen).
            </div>

            <Button
              variant="sw-default"
              disabled={busy}
              style={{ width: "100%" }}
              onClick={() =>
                onRequestHouseholdFieldMerge(householdCluster, {
                  defaultSurvivorId: householdCluster[1]!.id,
                  initialReason:
                    "Mortgage-import placeholder is a second member of this household — merged in, both people kept.",
                })
              }
            >
              Different people, same household &mdash; put them together&hellip;
            </Button>
            <div className="cu-dr-s58">
              A spouse or family member who does not share the surname.
              Both people stay, in one household, and the loan sits with
              the family it belongs to.
            </div>

            <Button
              variant="sw-default"
              disabled={busy}
              style={{ width: "100%" }}
              onClick={() =>
                act(
                  "dismiss",
                  false,
                  undefined,
                  "Not the same family — the address is shared but the households are separate. The placeholder stays its own household.",
                  "data_quality_cleanup",
                )
              }
            >
              Not the same family &mdash; leave them apart
            </Button>
            <div className="cu-dr-s59">
              A house that changed hands, or two unrelated people at one
              address. Nothing merges, and this stops being asked about.
              Reversible for 60 seconds, and anyone can reopen it later.
            </div>
          </div>
        </div>
      )}

      {householdCluster && !stubWithCandidate && (
        <div className="cu-dr-s32">
          <Button
            variant="primary"
            onClick={() =>
              onRequestHouseholdFieldMerge(householdCluster, {
                // Pass the detector's recommended survivor explicitly so the
                // overlay opens on the field review with the right household
                // pre-selected — no reliance on the "· stub" label text
                // (which is now plain English). app.params.survivor_id comes
                // from recommendation.params.survivor_id.
                ...(typeof app.params?.survivor_id === "string"
                  ? { defaultSurvivorId: app.params.survivor_id }
                  : {}),
              })
            }
            disabled={busy}
            style={{ width: "100%" }}
          >
            {householdClusterUnconfirmed
              ? "This is one family — review & merge…"
              : "Review every field, then merge…"}
          </Button>
          <div className="cu-dr-s47">
            Opens a side-by-side of{" "}
            <b>{displayLabel(householdCluster[0]!.label)}</b> and{" "}
            <b>{displayLabel(householdCluster[1]!.label)}</b>.{" "}
            {householdClusterUnconfirmed ? (
              <>
                Only use this once you've confirmed it's really one
                family — same address with different surnames is often
                two unrelated households. You'll pick who survives and
                see every field before anything happens.
              </>
            ) : (
              <>
                You'll see every field before anything happens — the
                recommended surviving household is pre-selected, and you
                can switch it with one click.
              </>
            )}{" "}
            Nothing changes until you confirm; reversible after.
          </div>
        </div>
      )}

      {/* Grant 2026-07-08 — the search-and-merge counterpart: this
          issue only names ONE household (spouse-pass, trust-reattach's
          review branch), so there's no known destination to open the
          per-field review overlay against yet. Opens a search UI to
          find it first (existing HouseholdMergePicker, wrapped for
          Cleanup — see CleanupHouseholdSearchMergeOverlay). */}
      {searchMergeSource && onRequestHouseholdSearchMerge && !quarantineIssue && (
        <div className="cu-dr-s32">
          <Button
            variant="primary"
            onClick={() => onRequestHouseholdSearchMerge(searchMergeSource)}
            disabled={busy}
            style={{ width: "100%" }}
          >
            {issue.recommendation?.label ||
              "Find & merge into an existing household…"}
          </Button>
          <div className="cu-dr-s47">
            Search for the destination household for{" "}
            <b>{displayLabel(searchMergeSource.label)}</b>, then review
            every field before merging. Nothing changes until you confirm;
            reversible after.
          </div>
          {/* Ryan 2026-08-25 — the third answer: the partner is real, just
              not in the system yet. "Find & merge" only helps if they're
              already here somewhere; opens the household's Add Member
              flow in a new tab so the steward finishes the job they were
              already doing (entering the spouse's details) instead of
              guessing which of the other two buttons is safe to click. */}
          {addMemberShown && addMember && (
            <div className="cu-dr-s57">
              <a
                href={`${recordDeepLink("household", addMember.householdId)}?addMember=1`}
                target="_blank"
                rel="noreferrer" className="cu-dr-s60">
                Add a household member…
                <ExternalLink size={13} strokeWidth={2} aria-hidden />
              </a>
              <div className="cu-dr-s47">
                Not in the system yet? Opens{" "}
                <b>{displayLabel(addMember.householdLabel)}</b> to add them
                as a new contact. Nothing here is decided for you — you
                fill in their details there, and this flag clears once the
                household has more than one member.
              </div>
            </div>
          )}
          {/* Donna 2026-07-30 — the other answer. This question is a
              yes/no, and until now only "yes" had a button: "no" existed
              only as the generic duplicate label "Different people — keep
              separate", below the fold under "Or do something else", which
              is not what a steward looking at ONE person is looking for
              ("the answer is no but there is nothing to click to answer
              this"). One click, own rationale, and it sticks — the
              reconciler leaves human-dismissed issues closed. */}
          {livesAloneShown && livesAlone && (
            <div className="cu-dr-s57">
              <Button
                variant="sw-default"
                onClick={() =>
                  act("dismiss", false, undefined, livesAlone.rationale)
                }
                disabled={busy}
                style={{ width: "100%" }}
              >
                {livesAlone.label}
              </Button>
              <div className="cu-dr-s47">
                {livesAlone.consequence}
              </div>
            </div>
          )}
          {/* Andrea 2026-08-27 — the fourth answer. Married, spouse is
              real, and will never be a Stewardship contact — not the
              same fact as "lives alone" (there is a partner) and not
              the same intent as "add a household member" (nobody is
              getting entered). Same one-click dismiss mechanism as
              lives-alone; only the words on the record differ, which
              is the entire point of adding this button. */}
          {spouseNotAClientShown && spouseNotAClient && (
            <div className="cu-dr-s57">
              <Button
                variant="sw-default"
                onClick={() =>
                  act("dismiss", false, undefined, spouseNotAClient.rationale)
                }
                disabled={busy}
                style={{ width: "100%" }}
              >
                {spouseNotAClient.label}
              </Button>
              <div className="cu-dr-s47">
                {spouseNotAClient.consequence}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Grant 2026-07-08 — the move counterpart: contact-no-household
          had no decide-rail handling at all (dead end). Opens a search
          UI to find the destination household and move the contact in
          (existing ContactMoveHouseholdModal, wrapped for Cleanup). */}
      {/* 2026-09-12 — "which family does this account belong to?" gets an
          answer button. `move-household` is recommended by
          record-on-archived-household, portfolio-wrong-household and
          mortgage-loan-wrong-household, and asked without a recommendation
          by financial-import-stub-review — and POST /api/cleanup/decide has
          no case for the verb, so every one of those recommended buttons
          came back 501 `verb_not_wired`. Production preview of
          financial-registration-repair on 2026-09-12: "accounts moved to
          the right family = 0, left for a human = 57". The 57 were refused
          on purpose (two households share the surname, or the registration
          names no person), so there is no target to recommend and the
          answer is in somebody's memory — which is exactly the case that
          needs a picker rather than a button. Opens
          MoveRecordHouseholdModal, which POSTs to the real, scope-gated,
          audited move endpoint and carries its own reason picker and undo
          toast. */}
      {recordMoveNeeded && onRequestRecordMove && (
        <div className="cu-dr-s32">
          <Button
            variant="primary"
            onClick={() => onRequestRecordMove(recordMoveNeeded)}
            disabled={busy}
            style={{ width: "100%" }}
          >
            Find the right household…
          </Button>
          <div className="cu-dr-s47">
            Search for the family{" "}
            <b>{displayLabel(recordMoveNeeded.record.label)}</b> belongs to
            and move it there. Its Ongoing Care plans, its Processing
            records and its client status go with it, and the move is
            undoable for 60 seconds.
          </div>
          {/* Grant 2026-09-16 — the second answer, and on a large share
              of these cards the right one. Reading the 83 open cards by
              name, many pairs are one person written twice: Jon Dekkers
              and Jonathan Dekkers, Nicholaus Smith and Nick Smith, Brad
              Allen and Bradley Allen. Moving a loan between two
              households that are one household files it under the
              duplicate and leaves the duplicate standing, which is
              exactly what the twelve reversed moves of 2026-09-15 did.

              Offered rather than decided, deliberately. The one thing
              that night proved is that guessing between "same person"
              and "two people" from a surname is what does the damage. */}
          {loanMergeAlternative && (
            <div className="cu-dr-s57">
              <Button
                variant="sw-default"
                disabled={busy}
                style={{ width: "100%" }}
                onClick={() =>
                  onRequestHouseholdFieldMerge(
                    [loanMergeAlternative.destination, loanMergeAlternative.source],
                    {
                      // The household holding the loan survives: it
                      // already carries the record, and on these pairs
                      // the other side is usually the thinner copy. One
                      // click swaps it in the review.
                      defaultSurvivorId: loanMergeAlternative.source.id,
                      initialReason:
                        "Two households holding one person under two spellings of their name — merged during mortgage cleanup.",
                    },
                  )
                }
              >
                Or: these two are the same person &mdash; merge the
                households&hellip;
              </Button>
              <div className="cu-dr-s47">
                Use this when{" "}
                <b>{displayLabel(loanMergeAlternative.source.label)}</b> and{" "}
                <b>{displayLabel(loanMergeAlternative.destination.label)}</b>{" "}
                are not two families but one person written down twice, a
                nickname or a middle initial apart. Moving the loan would
                file it under the duplicate and leave the duplicate
                standing. You&rsquo;ll see every field side by side first,
                and it is reversible after.
              </div>
            </div>
          )}
        </div>
      )}

      {contactMoveTarget && onRequestContactMove && (
        <div className="cu-dr-s32">
          <Button
            variant="primary"
            onClick={() => onRequestContactMove(contactMoveTarget)}
            disabled={busy}
            style={{ width: "100%" }}
          >
            Find a household to move into…
          </Button>
          <div className="cu-dr-s47">
            Search for the right household for{" "}
            <b>{displayLabel(contactMoveTarget.label)}</b> and move them
            in. Household-scoped records stay put; only the contact moves.
          </div>
        </div>
      )}

      {/* Nate 2026-07-09 — realty-sr-needs-client-household: link the
          service record to its real client household. Opens a household
          picker (CleanupRealtyLinkOverlay) with two paths — link to an
          existing household, or create a realty-only client from the
          synthetic stub. This is the button that was missing entirely, so
          "Pick the actual client household" POSTed a param-less match and
          dead-ended. */}
      {/* Grant 2026-08-18 — "the carrier sent a policy we don't have."
          The recommended action IS to create it, but a policy can't
          exist without a household and choosing the household is the
          judgment this item exists for. So the primary button opens the
          picker (CleanupIvansCreatePolicyOverlay) instead of POSTing a
          param-less verb — the same shape as the realty link below.
          Everything on the record comes from the carrier's own
          download; the picker supplies only the destination. */}
      {createPolicyTarget && onRequestCreatePolicy && (
        <div className="cu-dr-s32">
          <Button
            variant="primary"
            onClick={() => {
              const name = ivansSuggestedClientName(issue);
              onRequestCreatePolicy(createPolicyTarget, {
                first: name.first,
                last: name.last,
                policyType: ivansSuggestedPolicyType(issue),
                household: ivansSuggestedHousehold(issue),
              });
            }}
            disabled={busy}
            style={{ width: "100%" }}
          >
            {issue.recommendation?.label || "Create this policy…"}
          </Button>
          <div className="cu-dr-s47">
            Pick the household (or create the client if they're new) — the
            carrier's own coverage, dates and premium are filled in for you.
            Reversible for 60 seconds.
          </div>
        </div>
      )}

      {realtyLinkTarget && onRequestRealtyLink && (
        <div className="cu-dr-s32">
          <Button
            variant="primary"
            onClick={() => onRequestRealtyLink(realtyLinkTarget)}
            disabled={busy}
            style={{ width: "100%" }}
          >
            {issue.recommendation?.label || "Pick the client household…"}
          </Button>
          <div className="cu-dr-s47">
            Find the household that bought/sold{" "}
            <b>{displayLabel(realtyLinkTarget.label)}</b> and link this record
            to them (or create a realty-only client). Reversible afterward.
          </div>
        </div>
      )}

      {/* Baker fix — confirm-tier needs a canonical reason CODE and
          the UI had no picker, so dismiss was a dead end. Picker
          renders whenever a code OR an override rationale is needed;
          on a failed submit it highlights in red to point at what's
          missing (UX rule #5 — no dead end). */}
      {showReasonInputs && (
        <div
          style={{
            marginBottom: 12,
            border: errHighlightsCode
              ? "1px solid var(--danger)"
              : "1px solid var(--mid-gray)",
            background: errHighlightsCode
              ? "rgba(192,57,43,0.04)"
              : "transparent",
            borderRadius: 8,
            padding: "10px 12px",
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: errHighlightsCode
                ? "var(--danger)"
                : "var(--blue-dark)",
              marginBottom: 6,
            }}
          >
            Reason for this action
            {/* Says "required" only when it actually is. On an exempt
                verb (dismiss / snooze / keep-current — nothing about a
                record changes) the picker stays available but optional,
                and labelling it "required" next to a live Apply button
                was its own small lie. — Grant, 2026-08-10 */}
            {issue.tier === "confirm" &&
            !REASON_EXEMPT_VERBS.has(activeVerb)
              ? " · required (type-to-confirm)"
              : " · optional"}
          </div>
          <select
            value={reasonCode}
            onChange={(e) => {
              setReasonCode(e.target.value);
              if (err) setErr(null);
            }}
            style={{
              width: "100%",
              border:
                errHighlightsCode && !reasonCode
                  ? "1px solid var(--danger)"
                  : "1px solid var(--mid-gray)",
              borderRadius: 6,
              padding: "6px 8px",
              fontSize: 12.5,
              background: "var(--bg-0)",
              color: "var(--navy)",
            }}
          >
            <option value="">Pick the reason…</option>
            {visibleReasonCodes.map((r) => (
              <option key={r.code} value={r.code}>
                {r.label}
              </option>
            ))}
          </select>
          {reasonCode && (
            <div className="cu-dr-s34">
              {
                visibleReasonCodes.find((r) => r.code === reasonCode)
                  ?.when
              }
            </div>
          )}
          {/* "Other" code on a non-override path still needs the
              short reason — render a textarea inline so the user
              isn't blocked. */}
          {!isOverride && reasonCode === "other_with_text" && (
            <textarea
              value={rationale}
              onChange={(e) => {
                setRationale(e.target.value);
                if (err) setErr(null);
              }}
              placeholder="Type a short reason."
              rows={2}
              style={{
                width: "100%",
                marginTop: 8,
                border: errHighlightsText
                  ? "1px solid var(--danger)"
                  : "1px solid var(--mid-gray)",
                borderRadius: 6,
                padding: 8,
                fontSize: 12.5,
              }}
            />
          )}
        </div>
      )}

      {/* Baker 2026-07-30 — commission-pending-stale: the recommendation
          says "review & process in the commission flow" but nothing on
          this screen linked there, and the subject (a commission_reports
          row) has no record page — so the issue was a dead end (UX rule
          #5). The flow link is the PRIMARY action (the review happens
          there, new tab so the queue keeps its place); the resolve
          button below is honestly relabeled to what it does. */}
      {commissionIssue && !hasChoice && (
        <div className="cu-dr-s32">
          <Button
            variant="primary"
            onClick={() =>
              window.open(
                commissionFlowPath(issue!),
                "_blank",
                "noopener,noreferrer",
              )
            }
            disabled={busy}
            style={{ width: "100%" }}
          >
            {COMMISSION_FLOW_LINK_LABEL} ↗
          </Button>
          <div className="cu-dr-s47">
            Opens the Commissions list (new tab), filtered to this
            report&rsquo;s company — that flow stays the system of record
            for the amounts. Open each disbursement&rsquo;s worksheet to
            check the numbers (Calculate / Approve / Mark Paid); the
            send-to-advisor step lives on the dashboard&rsquo;s Commission
            Queue. Once it&rsquo;s handled, come back and mark this
            resolved below.
          </div>
        </div>
      )}

      {/* Primary action = APPLY THE RECOMMENDATION. Hidden once the
          steward picks a disposition from "or do something else" (that
          choice gets its own Apply button below) so there's never a
          double Apply. */}
      {app.ok &&
      !householdCluster &&
      !realtyLinkTarget &&
      !createPolicyTarget &&
      !hasChoice ? (
        <Button
          variant={cluster || commissionIssue ? "sw-default" : "primary"}
          onClick={() => act(app.verb, false)}
          disabled={
            busy ||
            needsReasonCode ||
            (advisorGapIssue && app.verb === "match" && !advisorId) ||
            // Expiration issues: block until the required date is
            // entered (missing-expiration), or when a typed date is
            // malformed (both) — never send a bad write.
            (expirationIssue &&
              app.verb === "verify" &&
              (expirationDate.trim()
                ? !isValidExpirationInput(expirationDate.trim())
                : expirationDateRequired(issue))) ||
            // Client-since issues: the date IS the action — block until
            // a valid one is entered (never send a bad write).
            (clientSinceIssue &&
              app.verb === "verify" &&
              !isValidClientSinceInput(clientSinceDate.trim())) ||
            // Effective-date issues: same doctrine — the date IS the
            // action.
            (effectiveDateIssue &&
              app.verb === "verify" &&
              !isValidEffectiveDateInput(effectiveDate.trim())) ||
            // PM-entity issues: the typed entity name IS the action —
            // block until one is entered (the escape button stays live).
            (pmEntityIssue &&
              app.verb === "verify" &&
              !isValidEntityNameInput(entityName)) ||
            // Program-type issues: the picked program IS the action —
            // block until one is picked (never send a bad write).
            (programTypeIssue &&
              app.verb === "verify" &&
              !isValidProgramTypeInput(programType)) ||
            // Marital issues: the picked status IS the action — block
            // until one is picked (the escape button stays live).
            (maritalIssue &&
              app.verb === "verify" &&
              !isValidMaritalInput(maritalStatus)) ||
            // Primary-contact issues: the picked member IS the action —
            // block until one is picked (the "we don't know" escape stays
            // live). Also covers the still-loading and no-members states,
            // where there is nothing valid to send.
            (primaryIssue &&
              app.verb === "verify" &&
              !isValidPrimaryPick(primaryPick, primaryCandidates)) ||
            // PM-fee issues: the typed amount IS the action — block
            // until a valid one is entered (the escape button stays
            // live).
            (pmFeeIssue &&
              app.verb === "verify" &&
              !isValidFeeInput(feeAmount)) ||
            // Realty zero-price: the typed price IS the action — block
            // until a valid one is entered ("Not an issue" stays live for
            // a transaction that genuinely wasn't a sale).
            (realtyPriceIssue &&
              app.verb === "verify" &&
              !isValidSalePriceInput(salePrice)) ||
            // Contact-method issues: at least one of email/phone IS the
            // action — block until one is entered (the "keep record
            // anyway" escape stays live for genuinely neither).
            (noContactMethodIssue &&
              app.verb === "verify" &&
              !contactEmail.trim() &&
              !contactPhone.trim())
          }
          style={{ width: "100%" }}
        >
          {busy
            ? "Applying…"
            : commissionIssue
              ? COMMISSION_APPLY_LABEL
              : advisorGapIssue && app.verb === "match"
              ? advisorId
                ? `Assign ${
                    advisorOptions.find((u) => u.id === advisorId)?.name ??
                    "advisor"
                  } as advisor`
                : "Assign as advisor (pick who first)"
              : expirationIssue && app.verb === "verify"
                ? expirationDate.trim()
                  ? `Set expiration to ${expirationDate.trim()} & resolve`
                  : expirationDateRequired(issue)
                    ? "Set the expiration (enter the date first)"
                    : "Verified — date on file is right"
                : clientSinceIssue && app.verb === "verify"
                  ? clientSinceDate.trim()
                    ? `Set client-since to ${clientSinceIsFloor ? "at least " : ""}${clientSinceDate.trim()} & resolve`
                    : "Set the client-since date (enter it first)"
                  : effectiveDateIssue && app.verb === "verify"
                    ? effectiveDate.trim()
                      ? `Set start date to ${effectiveDate.trim()} & resolve`
                      : "Set the start date (enter it first)"
                    : pmEntityIssue && app.verb === "verify"
                      ? isValidEntityNameInput(entityName)
                        ? `Create “${entityName.trim()}” & move the loans`
                        : "Create the entity (type its name first)"
                      : programTypeIssue && app.verb === "verify"
                        ? isValidProgramTypeInput(programType)
                          ? `Set program to ${programTypeLabel(programType.trim())} & resolve`
                          : "Set the loan program (pick it first)"
                        : primaryIssue && app.verb === "verify"
                          ? primaryPickApplyLabel(
                              primaryPick,
                              primaryCandidates,
                            )
                        : maritalIssue && app.verb === "verify"
                          ? isValidMaritalInput(maritalStatus)
                            ? `Set marital status to ${maritalStatus.trim()} & resolve`
                            : "Set the marital status (pick it first)"
                          : realtyPriceIssue && app.verb === "verify"
                          ? isValidSalePriceInput(salePrice)
                            ? `Set sale price to $${salePrice.trim().replace(/^\$/, "")} & resolve`
                            : "Set the sale price (enter it first)"
                          : pmFeeIssue && app.verb === "verify"
                            ? isValidFeeInput(feeAmount)
                              ? `Set amount earned to $${feeAmount.trim().replace(/^\$/, "")} & resolve`
                              : "Set the amount earned (enter it first)"
                            : noContactMethodIssue && app.verb === "verify"
                              ? contactEmail.trim() || contactPhone.trim()
                                ? "Save contact method & resolve"
                                : "Enter an email or phone (at least one first)"
                              : cluster
                                ? "Quick-merge with recommended defaults"
                                : `Apply${recVerb ? ` — ${issue.recommendation?.label}` : ""}`}
        </Button>
      ) : /* "Needs full review" explainer — ONLY for a real
            recommendation that can't be one-clicked here (e.g. a
            move/restore verb). Never for the no-recommendation case
            (the couple) — that path has no dead end; the disposition
            buttons below are the answer. Also suppressed when a contact
            or household cluster button is already showing the review
            path, or once the steward has chosen a disposition. */
      !app.ok &&
        !hasChoice &&
        !app.noRecommendation &&
        !cluster &&
        !householdCluster &&
        !searchMergeSource &&
        !contactMoveTarget &&
        // The record-move picker is a real action; don't also tell the
        // user no action is available (UX rule #5 — no dead ends).
        !recordMoveNeeded &&
        // hh-no-primary renders its own dedicated block (member picker +
        // "we don't know" escape) above — don't also show the generic
        // note. Belt-and-braces: its applicability is ok:true now, so
        // this branch isn't reached for it anyway.
        !primaryIssue &&
        app.why ? (
        <div className="cu-dr-s61">
          {app.why}
        </div>
      ) : null}

      {/* Override / other actions. Choosing a non-recommended action
          requires a one-line rationale (UX rule #6). */}
      <div className="cu-dr-s62">
        <div className="cu-dr-s63">
          Or do something else
        </div>
        <div className="cu-dr-s64">
          {/* Donna's request — fix the record's core fields (name /
              email / phone / address) in place. Opens the record drawer
              straight into its inline editor, so a simple correction
              never means leaving Cleanup to hunt for "Edit details" on
              the full record. Only when the issue is about one editable
              record (a cluster edits each record via its name). */}
          {onRequestEdit && singleEditable && (
            <Button
              variant="sw-default"
              size="sm"
              disabled={busy}
              onClick={() => onRequestEdit(singleEditable)}
            >
              <Pencil
                size={13}
                strokeWidth={1.9}
                aria-hidden
                style={{ marginRight: 5, verticalAlign: "-2px" }}
              />
              Edit {singleEditable.kind === "household"
                ? "household"
                : "contact"}{" "}
              details…
            </Button>
          )}
          {/* Step 4 — for a duplicate, the dismiss verb IS the "these
              are different people, keep them separate" escape hatch
              (and it sticks — the pair isn't re-flagged). Label it that
              way so it's an obvious answer, not a vague "dismiss". */}
          {/* Donna 2026-07-30 — suppressed when the "No — they're on their
              own" button is already showing. Both are the dismiss verb, but
              "Different people — keep separate" is written for a two-record
              duplicate; on a single-member household there are no
              "different people", and offering both made the real answer
              harder to find, not easier. */}
          {/* Cambria 2026-07-31 — same reasoning for the twin picker's
              "None of these — it's a different person" button: both are
              the dismiss verb, and offering a second, vaguer wording of
              an answer already stated above makes the real one harder to
              find, not easier. */}
          {!livesAloneShown && !twinPick && (
            <Button
              variant="sw-default"
              size="sm"
              disabled={busy}
              onClick={() => {
                setChosen("dismiss");
                if (issue.issueFamily === "duplicate" && !rationale.trim()) {
                  setRationale("Different people — not a duplicate (kept separate).");
                }
              }}
            >
              {issue.issueFamily === "duplicate"
                ? "Different people — keep separate"
                : "Not an issue"}
            </Button>
          )}
          {/* Joy 2026-07-02 — the couple answer ("husband and wife: do
              I merge?" → no). Keeps both PEOPLE as separate contacts
              and combines their two households into one, via the
              existing survivor-pick + per-field household merge
              overlay. The flag text always said "link them as a
              household"; this is the button that does it.

              Grant 2026-08-10: when linkHhPlan (pre-checked above,
              quietly, when the card opened) already shows the pair is
              in ONE household, offering "link as one household…" reads
              as a live action when it isn't — clicking it just tells you
              what this label already says. Swap to a button that names
              the true state and one-clicks straight to the answer;
              linkAsHousehold() still re-verifies live before deciding
              anything, so a stale pre-check can't cause a wrong action. */}
          {canOfferLinkHousehold(issue) && (
            <Button
              variant="sw-default"
              size="sm"
              disabled={busy || linkHhBusy}
              onClick={() => void linkAsHousehold()}
            >
              {linkHhBusy
                ? linkHhPlan?.kind === "same"
                  ? "Confirming…"
                  : "Looking up households…"
                : linkHhPlan?.kind === "same"
                  ? "A couple — keep both (already one household)"
                  : "A couple — link as one household…"}
            </Button>
          )}
          <Button
            variant="sw-default"
            size="sm"
            disabled={busy}
            onClick={() => setChosen("snooze")}
          >
            Snooze 24h
          </Button>
          {/* Donna's request — set the item aside while waiting on an
              answer (e.g. asked an advisor about a contact). It leaves
              the active lane and lands in "Waiting on follow-up", with a
              note of what you're waiting on; bring it back when you hear. */}
          <Button
            variant="sw-default"
            size="sm"
            disabled={busy}
            onClick={() => setFollowupOpen((v) => !v)}
          >
            Waiting on an answer…
          </Button>
          {/* Donna 2026-07-14 — hand this issue to a teammate without
              leaving the record. Parity with the /cleanup workspace,
              which could already assign; the in-record drawer couldn't. */}
          <Button
            variant="sw-default"
            size="sm"
            disabled={busy}
            onClick={() => setAssignOpen((v) => !v)}
          >
            {issue.assignedOwnerUserId
              ? "Reassign to a teammate…"
              : "Assign to a teammate…"}
          </Button>
          {/* Vendor escape hatches — only on contact clusters with 2+
              members. "One is a vendor" picks which; "Both are vendors"
              (step 4 — Baker's title-officers-share-a-phone case) retags
              every member and keeps them separate, no single-pick. */}
          {issue.entityKind === "contact" &&
            issue.affectedRecords.filter((r) => r.kind === "contact").length >= 2 && (
              <>
                <Button
                  variant="sw-default"
                  size="sm"
                  onClick={() => {
                    setChosen("retag-as-vendor");
                    setVendorAllMode(false);
                    setVendorPickerOpen(true);
                  }}
                  disabled={busy}
                >
                  One is a vendor…
                </Button>
                <Button
                  variant="sw-default"
                  size="sm"
                  onClick={() => {
                    setChosen("retag-as-vendor");
                    setVendorAllMode(true);
                    setVendorPickerOpen(false);
                    if (!rationale.trim()) {
                      setRationale(
                        "Both are vendors (e.g. same office) — kept separate.",
                      );
                    }
                  }}
                  disabled={busy}
                >
                  Both are vendors
                </Button>
              </>
            )}
        </div>

        {/* Joy 2026-07-02 — outcome of the household lookup when it
            couldn't open the merge overlay: already one household /
            a contact with no household / stale records. Inline, with
            the next step named (UX rule #5 — no dead end). */}
        {linkHhNote && (
          <div
            style={{
              marginTop: 10,
              padding: "8px 10px",
              borderRadius: 8,
              fontSize: 12.5,
              lineHeight: 1.45,
              color: "var(--navy)",
              border:
                linkHhNote.tone === "warn"
                  ? "1px solid var(--danger)"
                  : "1px solid var(--blue)",
              background:
                linkHhNote.tone === "warn"
                  ? "rgba(192,57,43,0.06)"
                  : "var(--blue-tint)",
            }}
          >
            {linkHhNote.text}
          </div>
        )}

        {/* Follow-up park form (Donna's request). */}
        {followupOpen && (
          <div className="cu-dr-s65">
            <div className="cu-dr-s23">
              Set aside while you wait on an answer
            </div>
            <div className="cu-dr-s51">
              Moves this to your <b>Waiting on follow-up</b> lane — out of
              the active queue, but easy to find. Bring it back any time.
            </div>
            <textarea
              value={followupReason}
              onChange={(e) => {
                setFollowupReason(e.target.value);
                if (err) setErr(null);
              }}
              rows={2}
              placeholder="What are you waiting on? (e.g. Asked Mike about this contact's info)" className="cu-dr-s66"
            />
            <Button
              variant="primary"
              onClick={park}
              disabled={busy}
              style={{ width: "100%" }}
            >
              {busy ? "Setting aside…" : "Move to Follow-up"}
            </Button>
          </div>
        )}

        {/* Donna 2026-07-14 — "Assign to a teammate" inline picker. Same
            mechanism as the workspace + RestrictedPanel (api.assignCleanupIssue
            → POST /issues/:id/assign). Assigning doesn't resolve the issue;
            it stays open and moves into the teammate's Cleanup queue. */}
        {assignOpen && (
          <div className="cu-dr-s65">
            <div className="cu-dr-s23">
              Hand this to a teammate
            </div>
            <div className="cu-dr-s51">
              Assigns this issue to another employee — it stays open and
              moves into their <b>Cleanup</b> queue. Nothing on the record
              changes; reassign or clear it any time.
            </div>
            {teammates.length === 0 ? (
              <div className="cu-dr-s67">
                Couldn't load the teammate list. Try reopening this, or add
                a note in research for whoever should pick it up.
              </div>
            ) : (
              <>
                <select
                  value={assignPickedId}
                  onChange={(e) => {
                    setAssignPickedId(e.target.value);
                    if (err) setErr(null);
                  }}
                  disabled={assignBusy} className="cu-dr-s9">
                  <option value="">Choose a teammate…</option>
                  {teammates.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name ?? u.email}
                      {u.role_slug ? ` · ${u.role_slug}` : ""}
                    </option>
                  ))}
                </select>
                <Button
                  variant="primary"
                  onClick={() => void assignToTeammate()}
                  disabled={assignBusy || !assignPickedId}
                  style={{ width: "100%" }}
                >
                  {assignBusy ? "Assigning…" : "Assign to teammate"}
                </Button>
              </>
            )}
          </div>
        )}

        {/* Success line after an assign — persists (the selected issue is
            unchanged, so per-issue state isn't reset). */}
        {assignMsg && (
          <div className="cu-dr-s68">
            {assignMsg}
          </div>
        )}

        {/* Step 4 — "both are vendors" confirmation (no single-pick). */}
        {chosen === "retag-as-vendor" && vendorAllMode && (
          <div className="cu-dr-s65">
            <div className="cu-dr-s23">
              Tag both as vendors &amp; keep separate
            </div>
            <div className="cu-dr-s51">
              Reclassifies each of these as <code>contact_type=vendor</code>.
              They stop appearing in person lists and drop out of duplicate
              clusters — so they stay separate, not merged.
            </div>
            <ul className="cu-dr-s69">
              {issue.affectedRecords
                .filter((r) => r.kind === "contact")
                .map((rec) => (
                  <li key={rec.id}>{displayLabel(rec.label ?? "") || rec.id}</li>
                ))}
            </ul>
          </div>
        )}

        {/* Baker 2026-05-29 — vendor picker. When the user chose
            "Retag as vendor", show an inline picker listing every
            contact member of the cluster. They pick which one is the
            vendor; that id flows into params.contact_id on submit. */}
        {chosen === "retag-as-vendor" && vendorPickerOpen && (
          <div className="cu-dr-s65">
            <div className="cu-dr-s23">
              Which record is the vendor?
            </div>
            <div className="cu-dr-s51">
              Reclassifies the chosen record as <code>contact_type=vendor</code>.
              It stops appearing in person-facing lists; the other
              cluster member stays as a person.
            </div>
            <div className="cu-dr-s24">
              {issue.affectedRecords
                .filter((r) => r.kind === "contact")
                .map((rec) => (
                  <label
                    key={rec.id}
                    className="cu-radio-pick-row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 8px",
                      borderRadius: 6,
                      cursor: "pointer",
                      background:
                        vendorPickedId === rec.id
                          ? "var(--blue-tint)"
                          : "transparent",
                      border:
                        vendorPickedId === rec.id
                          ? "1px solid var(--blue)"
                          : "1px solid transparent",
                    }}
                  >
                    <input
                      type="radio"
                      name="vendor-pick"
                      value={rec.id}
                      checked={vendorPickedId === rec.id}
                      onChange={() => setVendorPickedId(rec.id)}
                      disabled={busy}
                    />
                    <span className="cu-dr-s70">
                      {displayLabel(rec.label ?? "") || rec.id}
                    </span>
                  </label>
                ))}
            </div>
          </div>
        )}

        {/* Baker 2026-06-27 — optional vendor classification. Shown for
            both "one is a vendor" and "both are vendors". Capture what
            KIND of vendor + which company in the same click; leaving them
            blank is fine (keeps the fast path one click). */}
        {chosen === "retag-as-vendor" && (vendorAllMode || vendorPickerOpen) && (
          <div className="cu-dr-s65">
            <div className="cu-dr-s71">
              Classify the vendor{" "}
              <span className="cu-dr-s72">
                (optional)
              </span>
            </div>
            <div className="cu-dr-s51">
              Note the kind of vendor and which company they relate to, so
              they're easy to find later. You can skip this.
            </div>
            <div className="cu-dr-s64">
              <label className="cu-dr-s73">
                <span className="cu-dr-s74">
                  Vendor type
                </span>
                <select
                  value={vendorSubtype}
                  onChange={(e) => setVendorSubtype(e.target.value)}
                  disabled={busy} className="cu-dr-s75">
                  <option value="">— Not specified —</option>
                  {VENDOR_SUBTYPES.map((s) => (
                    <option key={s} value={s}>
                      {VENDOR_SUBTYPE_LABELS[s]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="cu-dr-s73">
                <span className="cu-dr-s74">
                  Associated company
                </span>
                <select
                  value={vendorCompanySlug}
                  onChange={(e) => setVendorCompanySlug(e.target.value)}
                  disabled={busy} className="cu-dr-s75">
                  <option value="">— Not specified —</option>
                  {VENDOR_COMPANY_OPTIONS.map((slug) => (
                    <option key={slug} value={slug}>
                      {companyLabel(slug)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}

        {/* Grant 2026-05-18: archiving must happen where the record's
            details are visible (the names here are identical — a blind
            "Archive Paul Theisen ×3" is meaningless). So archive lives
            in the record drawer: click a record's name above to open
            it, see its full identity + what's attached, then archive
            it there if it's a throwaway. */}
        {issue.affectedRecords.length > 0 && (
          <div className="cu-dr-s76">
            <b className="cu-dr-s77">
              Is one record junk (just a name + phone, nothing
              attached)?
            </b>{" "}
            Click its name in the research pane to open it — you’ll see
            its full details and an <b>Archive</b> action there, so you
            know exactly what you’re archiving. Reversible.
          </div>
        )}
        {/* The chosen disposition's Apply button. Renders for ANY
            choice from "or do something else" — not only overrides — so
            "Different people — keep separate" on a no-recommendation
            issue (the couple) always has a button to commit. Before the
            Baker 2026-06-26 fix, "keep separate" set chosen="dismiss",
            which equaled the fallback app.verb, so isOverride was false
            and this block never rendered: the dead end. */}
        {hasChoice && (
          <div className="cu-dr-s57">
            {/* Only a genuine override of a recommendation needs a typed
                one-line rationale (UX rule #6). A plain disposition on a
                no-recommendation issue uses the canonical reason CODE
                picker above (confirm-tier) instead. */}
            {isOverride && (
              <>
                <div className="cu-dr-s78">
                  You’re choosing “{choiceVerbNoun(activeVerb, issue.issueFamily)}”
                  instead of the recommendation — a one-line reason
                  (audit-logged):
                </div>
                <textarea
                  value={rationale}
                  onChange={(e) => {
                    setRationale(e.target.value);
                    if (err) setErr(null);
                  }}
                  placeholder="Why this instead of the recommended action?"
                  rows={2}
                  style={{
                    width: "100%",
                    border: errHighlightsText
                      ? "1px solid var(--danger)"
                      : "1px solid var(--mid-gray)",
                    borderRadius: 6,
                    padding: 8,
                    fontSize: 12.5,
                  }}
                />
              </>
            )}
            <Button
              variant="primary"
              onClick={() => act(activeVerb, isOverride)}
              disabled={
                busy ||
                needsRationale ||
                needsReasonCode ||
                // Baker 2026-05-29 — single-vendor retag needs the
                // picker answered first. "Both are vendors" (all-mode)
                // needs no pick, so it's exempt.
                (activeVerb === "retag-as-vendor" &&
                  !vendorAllMode &&
                  !vendorPickedId)
              }
              style={{ marginTop: isOverride ? 8 : 0, width: "100%" }}
            >
              {busy
                ? "Applying…"
                : choiceApplyLabel(
                    activeVerb,
                    issue.issueFamily,
                    vendorAllMode,
                  )}
            </Button>
            {/* No dead end (UX rule #5): if the button is disabled
                because a required reason code is still unpicked, say so
                and point up at the picker. */}
            {!busy && needsReasonCode && (
              <div className="cu-dr-s79">
                Pick a reason above first — this action needs one.
              </div>
            )}
          </div>
        )}
      </div>

      {err && (
        <div className="cu-dr-s80">
          {err}
        </div>
      )}

      {/* Every issue gets a note box, not only the ones you can't act on
          (Mylah, 2026-08-19). See IssueNoteBox. */}
      <IssueNoteBox issue={issue} />
    </Rail>
  );
}

function Rail({ children }: { children: React.ReactNode }) {
  return (
    // `cu-rail` exists so the stylesheet can let long button labels WRAP
    // in this column (Mylah, 2026-08-19). Every button in the OS is a
    // fixed-height single line with white-space: nowrap — correct for
    // "Save" and "Cancel", wrong here, where a label carries a household
    // name ("Create this policy on Rene Carder Household") and gets
    // clipped at BOTH ends by the centering. She could not read what the
    // button she was being asked to press actually did.
    <div className="cu-rail">
      {children}
    </div>
  );
}
